import Phaser from "phaser";
import { Client, Room } from "colyseus.js";
import {
  GAME_CONFIG, IChatMessage, tileAt, isSolidAt, JOBS, expForLevel,
  NPCS, NpcDef, QuestMap, TUTORIAL_STEPS, FIRST_TUTORIAL_STEP_ID, ItemEffect,
  ZONES, ZONE_IDS, ZoneId, DEFAULT_ZONE, TILE, getBuildings, COMBAT, ProjectTemplate,
  getMonsterTemplate, MONSTERS, SKILL_LEARN_STAMINA_COST, STAMINA, TravelMethod, TRAVEL_METHODS,
  DUPLICATE_LOGIN_LEAVE_CODE, DUPLICATE_LOGIN_MSG,
} from "@ordinarydeveloper/shared";
import { SERVER_URL, API_URL, apiFetch, setOnUnauthorized } from "../serverUrl";
import { SIDE_JOB_WARNING } from "@ordinarydeveloper/shared";
import VirtualJoystick from "phaser3-rex-plugins/plugins/virtualjoystick.js";

interface ProjectEventChoiceView {
  id: string;
  label: string;
}

interface ActiveProjectView {
  id: number;
  templateId: number;
  title: string;
  description: string;
  progress: number;
  workUnits: number;
  rewardGold: number;
  deadlineAt: string;
  status: "active" | "completed" | "failed";
  pendingEvent: { id: string; situation: string; choices: ProjectEventChoiceView[] } | null;
  assigned: boolean;
  sideJob: boolean;
}

interface CompanyView {
  id: number;
  name: string;
  description: string;
  zone: string;
  salary: number;
  minLevel: number;
  minRank?: string;
  eligible: boolean;
  lockReasons: string[];
  current: boolean;
}

interface EmploymentStatus {
  employed: boolean;
  company: CompanyView | null;
  performanceScore: number;
  nextSalaryAt: string | null;
  companies: CompanyView[];
  nextGoal: string | null;
}

interface InvItem {
  id: string;
  name: string;
  icon: string;
  description: string;
  effect: ItemEffect;
  quantity: number;
}

type SkillBonusType = "work_efficiency" | "hunt_attack" | "hunt_crit_chance";

interface SkillView {
  id: number;
  name: string;
  description: string;
  durationMinutes: number;
  requiredLevel: number;
  prerequisiteSkillId: number | null;
  bonusType: SkillBonusType;
  bonusValue: number;
  textbookName: string;
  textbookCost: number;
  status: "completed" | "learning" | "available" | "locked";
  remainingMs?: number;
  lockedReason?: string;
}

function skillBonusLabel(type: SkillBonusType, value: number): string {
  if (type === "work_efficiency") return `작업 효율 +${value}%`;
  if (type === "hunt_attack") return `사냥 공격력 +${value}`;
  return `사냥 치명타 확률 +${value}%`;
}

function skillStatusIcon(status: SkillView["status"]): string {
  if (status === "completed") return "✅";
  if (status === "learning") return "⏳";
  if (status === "locked") return "🔒";
  return "🔓";
}

interface ExamQuestionView {
  id: number;
  text: string;
  choices: string[];
}

interface ExamStatusView {
  rank: string;
  alreadyPromoted: boolean;
  eligible: boolean;
  reasons: string[];
  cooldownRemainingMs: number;
  skillsCompleted: number;
  skillsRequired: number;
  staminaCost: number;
  inProgress: ExamQuestionView[] | null;
}

interface ExamResultView {
  passed: boolean;
  correctCount: number;
  total: number;
  passCount: number;
  newRank?: string;
  staminaMaxBonus?: number;
  cooldownUntil?: string;
}

interface CodexView {
  entries: Record<string, { killCount: number; firstKillAt: string }>;
  discoveredCount: number;
  totalCount: number;
  completed: boolean;
}

interface DailyQuestView {
  id: number;
  questType: "hunt" | "work";
  monsterId: string | null;
  label: string;
  target: number;
  progress: number;
  claimed: boolean;
  rewardGold: number;
  rewardExp: number;
}

interface DailyQuestsState {
  quests: DailyQuestView[];
  allClaimed: boolean;
}

interface DashboardData {
  nickname: string;
  rank: string;
  headline: string;
  greetingLine: string;
  stamina: { value: number; max: number; msUntilFull: number };
  gold: number;
  employment: {
    employed: boolean; companyName: string | null; performanceScore: number;
    nextSalaryAt: string | null; nextGoal: string | null;
  };
  project: { active: ActiveProjectView | null; marketCount: number };
  daily: DailyQuestsState | null;
  settlement: { totalGold: number; items: { title: string; netGold: number; taxWithheld: number }[] } | null;
  skillLearning: { skillId: number; name: string; remainingMs: number } | null;
  notifications: string[];
}

function effectSummary(effect: ItemEffect): string {
  if (effect.type === "heal") return `HP +${effect.amount} 회복`;
  if (effect.type === "stamina") {
    return `집중력 +${effect.amount} 회복` + (effect.dailyLimit ? ` (일 ${effect.dailyLimit}회 제한)` : "");
  }
  return `이동속도 +${Math.round((effect.multiplier - 1) * 100)}% (${effect.durationMs / 1000}초)`;
}

// "⚡ Nickname" — job icon prefix for the floating name label
function playerLabel(nickname: string, job: string): string {
  const icon = JOBS[job]?.icon ?? "";
  return icon ? `${icon} ${nickname}` : nickname;
}

const { TILE_SIZE, MAP_COLS, MAP_ROWS, PLAYER_SPEED } = GAME_CONFIG;

// Indices into assets/game_tiles.png — a mini-atlas extracted from
// Modern_Exteriors_Complete_Tileset.png (16x16). The full 2816x8224 sheet
// exceeds common WebGL texture limits (8192), so we ship only the tiles we use.
const T = {
  GRASS: 0,       // src (9,1)   — base ground (both zones)
  GRASS_VAR: 1,   // src (10,3)
  ASPHALT: 5,     // src (36,1)  — road
  BUSH: 6,        // src (12,10) — tree (solid)
  ROOF: 8,        // src (67,67) — building / border rooftop (solid)
  SLAB_TL: 10,    // src (34,2)  — plaza slab 2x2 pattern
  SLAB_TR: 11,    // src (35,2)
  SLAB_BL: 12,    // src (34,3)
  SLAB_BR: 13,    // src (35,3)
  SIDEWALK: 15,   // herringbone pavement
  DIRT: 16,       // construction-site soil
  FENCE: 17,      // construction-site hazard barrier (solid)
} as const;

const BUILDING_SPRITES = [
  "house_twin", "house_small", "store_blue", "store_brown", "market_blue", "market_brown",
] as const;

interface OtherPlayer {
  container: Phaser.GameObjects.Container;
  hpFill: Phaser.GameObjects.Rectangle;
}

interface MonsterView {
  container: Phaser.GameObjects.Container;
  hpFill: Phaser.GameObjects.Rectangle;
}

const HP_BAR_W = 14;
// 사이드 팝업(우측 패널 5종 + 좌측 데일리) — 공통 모달 로직(togglePanel)이 관리한다
type SidePanelName = "project" | "skill" | "travel" | "codex" | "inventory" | "daily";
// 가상 조이스틱 배치 (화면 픽셀 기준) — 모바일은 채팅이 기본 접힘이라 하단이
// 비므로 좌하단에 바로 둔다 (우하단 💬 토글 버튼과는 좌우로 분리).
const JOY_BASE_RADIUS = 52;
const JOY_THUMB_RADIUS = 24;
const JOY_MARGIN_X = 86;
const JOY_BOTTOM_MARGIN = 120;
// Camera zoom is fixed at 2 (see setZoom below) — text canvases need to be
// rendered at zoom * devicePixelRatio or they blur when the camera scales them up.
const TEXT_RESOLUTION = (window.devicePixelRatio || 1) * 2;
// Name-label world font size — at 2x camera zoom this renders as ~14.4px on
// screen (6px * 1.2 — a 20% screen-size bump over the previous 12px).
const LABEL_FONT_SIZE = "7.2px";

export class GameScene extends Phaser.Scene {
  private room!: Room;
  private localPlayer!: Phaser.GameObjects.Container;
  private localHpFill!: Phaser.GameObjects.Rectangle;
  private otherPlayers: Map<string, OtherPlayer> = new Map();
  private monsters: Map<string, MonsterView> = new Map();

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<"up" | "down" | "left" | "right", Phaser.Input.Keyboard.Key>;
  // 모바일 가상 조이스틱 — 터치 디바이스에서만 생성 (PC는 null 유지)
  private joystick: VirtualJoystick | null = null;
  private joyLayoutPending = false;
  private viewportChangeHandler: (() => void) | null = null;
  private huntKey!: Phaser.Input.Keyboard.Key;
  private inventoryKey!: Phaser.Input.Keyboard.Key;

  // Hunt prompt ("사냥하기") — nearest alive monster in range, if any
  private huntTargetId: string | null = null;
  private huntPrompt!: HTMLDivElement;

  // Inventory / items
  private inventoryPanel!: HTMLDivElement;
  private inventoryList!: HTMLDivElement;
  private tooltip!: HTMLDivElement;
  private get inventoryOpen() { return this.openPanel === "inventory"; }
  private speedMultiplier = 1;
  private speedBuffUntil = 0;

  // Which zone (Colyseus room) this scene instance is connected to
  private zone: ZoneId = DEFAULT_ZONE;
  private get travelPanelOpen() { return this.openPanel === "travel"; }
  private travelPanel!: HTMLDivElement;
  private travelPanelBody!: HTMLDivElement;

  private lastSentPos = { x: 0, y: 0 };
  private nickname  = "";
  private job       = "";
  private joinToken = "";

  // Chat DOM
  private chatBox!: HTMLDivElement;
  private chatInput!: HTMLInputElement;
  // 모바일 채팅 접기 — PC(isTouchUi=false)에서는 항상 펼침(chatOpen=true) 고정
  private chatWrap!: HTMLDivElement;
  private chatOpen = true;
  private chatUnread = 0;
  private chatBadge!: HTMLSpanElement;
  private chatToggleBtn!: HTMLButtonElement;
  private chatPreview!: HTMLDivElement;
  private chatPreviewTimer: Phaser.Time.TimerEvent | null = null;

  // Battle log DOM (above chat)
  private battleLog!: HTMLDivElement;

  // Freelance projects
  private projectPanel!: HTMLDivElement;
  private projectPanelHeader!: HTMLDivElement;
  private projectPanelBody!: HTMLDivElement;
  private get projectPanelOpen() { return this.openPanel === "project"; }
  // 본업(배정 업무/프리 의뢰)과 부업 — 슬롯 2개가 공존한다
  private projectMain: ActiveProjectView | null = null;
  private projectSide: ActiveProjectView | null = null;
  private projectTab: "main" | "side" = "main";
  private displayedProject: ActiveProjectView | null = null; // 마감 카운트다운 대상
  private projectMarket: ProjectTemplate[] = [];
  private employment: EmploymentStatus | null = null;
  private projectDeadlineText: HTMLDivElement | null = null;
  private projectEventModal!: HTMLDivElement;
  private projectEventSituation!: HTMLDivElement;
  private projectEventChoices!: HTMLDivElement;

  // Generic confirm modal (replaces window.confirm)
  private confirmModal!: HTMLDivElement;
  private confirmMessage!: HTMLDivElement;
  private confirmYesBtn!: HTMLButtonElement;
  private confirmNoBtn!: HTMLButtonElement;

  // 전직 시험 모달 (인사팀 차장 NPC 대화 종료 시 열림)
  private examModal!: HTMLDivElement;
  private examBody!: HTMLDivElement;
  private examOpen = false;
  private examSelections = new Map<number, number>();
  private examCooldownEl: HTMLDivElement | null = null;
  private examCooldownUntilMs = 0;

  // Skill tree
  private skillPanel!: HTMLDivElement;
  private skillPanelBody!: HTMLDivElement;
  private get skillPanelOpen() { return this.openPanel === "skill"; }
  private skillList: SkillView[] = [];
  private skillAnyLearning = false;
  private skillCountdownEls = new Map<number, HTMLDivElement>();

  // 일일 버그 리포트 (좌측 트래커 — 튜토리얼 완료자만 서버가 상태를 보낸다)
  // 기본은 접힘(📋 배지 버튼)이고 탭하면 패널이 펼쳐진다 (PC/모바일 동일).
  private dailyTracker!: HTMLDivElement;
  private dailyBadgeBtn!: HTMLButtonElement;
  private dailyBadge!: HTMLSpanElement;
  private dailyState: DailyQuestsState | null = null;

  // 몬스터 도감
  private codexPanel!: HTMLDivElement;
  private codexPanelHeader!: HTMLDivElement;
  private codexPanelBody!: HTMLDivElement;
  private get codexPanelOpen() { return this.openPanel === "codex"; }

  // Dashboard ("오늘의 내 상황")
  private dashboardOverlay!: HTMLDivElement;
  private dashboardOpen = false;
  private dashboardKey!: Phaser.Input.Keyboard.Key;

  // 공통 모달 상태 — 사이드 팝업은 동시에 하나만 열리며, 열림 중에는
  // backdrop이 깔리고(바깥 탭 = 닫기) 캔버스 이동 입력이 차단된다.
  private readonly isTouchUi = GameScene.isTouchDevice();
  private openPanel: SidePanelName | null = null;
  private modalBackdrop!: HTMLDivElement;

  // 중복 접속으로 밀려나는 중 — 중복 처리(401 다발 등) 방지 플래그
  private forcedOut = false;

  // HUD DOM
  private hudLevel!: HTMLDivElement;
  // 직급 (수습/주니어) — 서버 "rank" 메시지로 수신, HUD 레벨 옆에 표시
  private rank = "";
  private lastLevel = 1;
  private lastExp = 0;
  private hudHpFill!: HTMLDivElement;
  private hudHpText!: HTMLDivElement;
  private hudStaminaFill!: HTMLDivElement;
  private hudStaminaText!: HTMLDivElement;
  private hudExpFill!: HTMLDivElement;
  private hudExpText!: HTMLDivElement;
  private hudGoldText!: HTMLDivElement;

  // Quest / dialogue
  private quests: QuestMap = {};
  private questTracker!: HTMLDivElement;
  private dialogueBox!: HTMLDivElement;
  private dialogueName!: HTMLDivElement;
  private dialogueText!: HTMLDivElement;
  private activeDialogue: { npc: NpcDef; index: number } | null = null;
  // NPCs whose dialogue was finished — don't reopen until the player walks away
  private dismissedNpcs = new Set<string>();

  // All manually-created DOM nodes, removed on scene shutdown
  private domNodes: HTMLElement[] = [];

  constructor() {
    super({ key: "GameScene" });
  }

  preload() {
    this.load.image("game_tiles", "assets/game_tiles.png");
    for (const name of BUILDING_SPRITES) {
      this.load.image(name, `assets/buildings/${name}.png`);
    }
  }

  private trackDom<T extends HTMLElement>(el: T): T {
    this.domNodes.push(el);
    return el;
  }

  create(data: { nickname?: string; job?: string; joinToken?: string; zone?: ZoneId } = {}) {
    this.nickname  = data.nickname  ?? `Player${Math.floor(Math.random() * 9000 + 1000)}`;
    this.job       = data.job       ?? "";
    this.joinToken = data.joinToken ?? "";
    this.zone      = data.zone      ?? DEFAULT_ZONE;
    this.forcedOut = false; // 씬 재시작(존 이동/재로그인) 시 초기화
    // 게임 중 API 401 = 세션 무효화(다른 기기 로그인) — 소켓 강제 종료와 동일 처리
    setOnUnauthorized(() => this.handleForcedLogout());
    this.showZoneBanner(ZONES[this.zone].name);

    this.buildTileMap();
    this.buildNpcs();
    this.createLocalPlayer(400, 320);
    this.setupInput();
    this.buildChatUI();
    this.buildBattleLog();
    this.buildModalBackdrop();
    this.buildHuntPrompt();
    this.buildLogoutButton();
    this.buildHud();
    this.buildQuestTracker();
    this.buildDailyTracker();
    this.buildDialogueBox();
    this.buildInventory();
    this.buildProjectPanel();
    this.buildProjectEventModal();
    this.buildConfirmModal();
    this.buildExamModal();
    this.buildSkillPanel();
    this.buildTravelPanel();
    this.buildCodexPanel();
    this.buildDashboardOverlay();
    this.buildDashboardHomeButton();
    this.connectServer();

    // Clean up DOM + socket when the scene stops (e.g. on logout)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());

    const worldW = MAP_COLS * TILE_SIZE;
    const worldH = MAP_ROWS * TILE_SIZE;
    this.cameras.main.setBounds(0, 0, worldW, worldH);
    this.cameras.main.startFollow(this.localPlayer, true, 0.08, 0.08);
    this.cameras.main.setZoom(2); // 32px effective tile size
    // 반드시 setZoom 이후 — 조이스틱 크기/좌표 계산이 카메라 줌에 의존한다
    this.buildJoystick();

    // Map/room load behind it — dashboard is the ①단계 "오늘의 내 상황" landing screen
    void this.openDashboard();
  }

  // ─── Map ─────────────────────────────────────────────────────────────────

  private buildTileMap() {
    const map = this.make.tilemap({
      tileWidth: TILE_SIZE, tileHeight: TILE_SIZE,
      width: MAP_COLS, height: MAP_ROWS,
    });
    const tileset = map.addTilesetImage("game_tiles", "game_tiles", TILE_SIZE, TILE_SIZE, 0, 0)!;
    const ground = map.createBlankLayer("ground", tileset)!;
    const deco   = map.createBlankLayer("deco", tileset)!;
    ground.setDepth(0);
    deco.setDepth(1); // above ground, below y-sorted sprites

    // Collision stays driven by the shared per-zone grid via isSolidAt() (client
    // movement + server validation) — this method only paints the visuals.
    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        const type = tileAt(this.zone, row, col);

        // Deterministic ground variant sprinkle (~1 in 7 tiles)
        const variant = (row * 31 + col * 17) % 7 === 0 ? 1 : 0;
        const grassTile = T.GRASS + variant;

        switch (type) {
          case TILE.BORDER:
            ground.putTileAt(T.ROOF, col, row);
            break;
          case TILE.BUILDING:
            // Ground painted plain — the actual building is a prefab sprite
            // image drawn separately below (roof + walls + door), not tiles.
            ground.putTileAt(grassTile, col, row);
            break;
          case TILE.ROAD:
            ground.putTileAt(T.ASPHALT, col, row);
            break;
          case TILE.SIDEWALK:
            ground.putTileAt(T.SIDEWALK, col, row);
            break;
          case TILE.PLAZA:
            // 2x2 slab pattern
            ground.putTileAt(T.SLAB_TL + (col % 2) + 2 * (row % 2), col, row);
            break;
          case TILE.DIRT:
            ground.putTileAt(T.DIRT, col, row);
            break;
          case TILE.FENCE:
            ground.putTileAt(T.DIRT, col, row);
            deco.putTileAt(T.FENCE, col, row);
            break;
          case TILE.TREE:
            ground.putTileAt(grassTile, col, row);
            deco.putTileAt(T.BUSH, col, row);
            break;
          default: // GRASS
            ground.putTileAt(grassTile, col, row);
        }
      }
    }

    // Building prefabs — complete roof+wall+door sprites anchored to the
    // bottom of their (solid) footprint, depth = bottom y so y-sorted
    // sprites behind them are hidden correctly. Name label sits centered
    // above the sprite's actual visual top (the art is taller than the
    // footprint) since the tileset has no baked-in Korean signage.
    for (const b of getBuildings(this.zone)) {
      const bottomY = (b.row + b.h) * TILE_SIZE;
      const img = this.add.image(b.col * TILE_SIZE, bottomY, b.sprite)
        .setOrigin(0, 1)
        .setDepth(bottomY);

      const topY = bottomY - img.displayHeight;
      const centerX = b.col * TILE_SIZE + img.displayWidth / 2;
      this.add.text(centerX, topY - 2, b.label, {
        fontSize: LABEL_FONT_SIZE, color: "#ffffff",
        stroke: "#000000", strokeThickness: 1,
      }).setOrigin(0.5, 1).setDepth(bottomY + 1).setResolution(TEXT_RESOLUTION);
    }
  }

  // ─── NPCs ────────────────────────────────────────────────────────────────

  private buildNpcs() {
    for (const npc of NPCS.filter((n) => n.zone === this.zone)) {
      const marker = this.add.rectangle(npc.x, npc.y, 12, 12, npc.color).setDepth(npc.y);
      marker.setStrokeStyle(1, 0xffffff, 0.4);
      this.add.text(npc.x, npc.y - 9, npc.label, {
        fontSize: LABEL_FONT_SIZE,
        color: "#ffffff",
        stroke: "#000000",
        strokeThickness: 1,
      }).setOrigin(0.5, 1).setDepth(npc.y).setResolution(TEXT_RESOLUTION);
    }
  }

  // ─── Players ─────────────────────────────────────────────────────────────

  private createLocalPlayer(x: number, y: number): void {
    const built = this.buildPlayerSprite(0xff6b6b, playerLabel(this.nickname, this.job));
    this.localPlayer = built.container;
    this.localHpFill = built.hpFill;
    this.localPlayer.setPosition(x, y);
    this.localPlayer.setDepth(y); // y-sort against buildings
    this.lastSentPos = { x, y };
  }

  private buildPlayerSprite(bodyColor: number, name: string):
    { container: Phaser.GameObjects.Container; hpFill: Phaser.GameObjects.Rectangle } {
    const body = this.add.rectangle(0, 0, 8, 10, bodyColor);
    const eyes = this.add.rectangle(0, -2, 5, 3, 0xffeaa7);
    // HP bar above head — fill is left-anchored so it drains left→right
    const hpBg   = this.add.rectangle(0, -9, HP_BAR_W, 2, 0x000000, 0.6);
    const hpFill = this.add.rectangle(-HP_BAR_W / 2, -9, HP_BAR_W, 2, 0x2ecc71).setOrigin(0, 0.5);
    const label = this.add.text(0, 6, name, {
      fontSize: LABEL_FONT_SIZE,
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 1,
    }).setOrigin(0.5, 0).setResolution(TEXT_RESOLUTION);

    const container = this.add.container(0, 0, [body, eyes, hpBg, hpFill, label]);
    return { container, hpFill };
  }

  private setHpBar(fill: Phaser.GameObjects.Rectangle, hp: number, maxHp: number) {
    const ratio = maxHp > 0 ? Phaser.Math.Clamp(hp / maxHp, 0, 1) : 0;
    fill.width = HP_BAR_W * ratio;
    fill.fillColor = ratio > 0.5 ? 0x2ecc71 : ratio > 0.25 ? 0xf39c12 : 0xe74c3c;
  }

  private spawnOtherPlayer(sessionId: string, x: number, y: number, nickname: string, job: string) {
    const { container, hpFill } = this.buildPlayerSprite(0x74b9ff, playerLabel(nickname, job));
    container.setPosition(x, y);
    container.setDepth(y); // y-sort against buildings
    this.otherPlayers.set(sessionId, { container, hpFill });
  }

  private removeOtherPlayer(sessionId: string) {
    const p = this.otherPlayers.get(sessionId);
    if (p) {
      p.container.destroy();
      this.otherPlayers.delete(sessionId);
    }
  }

  // ─── Monsters ────────────────────────────────────────────────────────────

  private spawnMonster(
    id: string, templateId: string, x: number, y: number, hp: number, maxHp: number, alive: boolean
  ) {
    const template = getMonsterTemplate(templateId);
    const color  = template?.color ?? 0x27c93f;
    const radius = template?.radius ?? 6;

    const body = template?.shape === "square"
      ? this.add.rectangle(0, 0, radius * 1.6, radius * 1.6, color)
      : this.add.circle(0, 0, radius, color);
    const eyeL   = this.add.circle(-radius * 0.35, -radius * 0.15, 1, 0x0a2a12);
    const eyeR   = this.add.circle(radius * 0.35, -radius * 0.15, 1, 0x0a2a12);
    const hpBg   = this.add.rectangle(0, -radius - 5, HP_BAR_W, 2, 0x000000, 0.6);
    const hpFill = this.add.rectangle(-HP_BAR_W / 2, -radius - 5, HP_BAR_W, 2, 0x2ecc71).setOrigin(0, 0.5);
    const nameLabel = this.add.text(0, -radius - 7, template?.label ?? "몬스터", {
      fontSize: LABEL_FONT_SIZE, color: "#ffffff",
      stroke: "#000000", strokeThickness: 1,
    }).setOrigin(0.5, 1).setResolution(TEXT_RESOLUTION);

    const container = this.add.container(x, y, [body, eyeL, eyeR, hpBg, hpFill, nameLabel]);
    container.setDepth(y); // y-sort against buildings
    container.setVisible(alive);
    this.monsters.set(id, { container, hpFill });
    this.setHpBar(hpFill, hp, maxHp);
  }

  private updateMonster(id: string, patch: { x?: number; y?: number; hp?: number; maxHp?: number; alive?: boolean }) {
    const view = this.monsters.get(id);
    if (!view) return;
    if (patch.x !== undefined) view.container.setX(patch.x);
    if (patch.y !== undefined) { view.container.setY(patch.y); view.container.setDepth(patch.y); }
    if (patch.alive !== undefined) view.container.setVisible(patch.alive);
    if (patch.hp !== undefined || patch.maxHp !== undefined) {
      const m = this.room.state.monsters.get(id);
      if (m) this.setHpBar(view.hpFill, m.hp, m.maxHp);
    }
  }

  // ─── Input ───────────────────────────────────────────────────────────────

  private setupInput() {
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasd = {
      up:    this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down:  this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left:  this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
    this.huntKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.inventoryKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.I);
    // Capture TAB so the browser doesn't shift focus away from the canvas
    this.input.keyboard!.addCapture(Phaser.Input.Keyboard.KeyCodes.TAB);
    this.dashboardKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TAB);

    // Event-driven (not JustDown-polled): a fast tap can land keydown+keyup in
    // the same input batch, where Key.onUp resets _justDown before update()
    // ever polls it — the "down" event always fires exactly once per press.
    this.huntKey.on("down", () => this.doHunt());
    this.inventoryKey.on("down", () => this.toggleInventory());
    this.dashboardKey.on("down", () => this.toggleDashboard());
  }

  // ─── 모바일 가상 조이스틱 ─────────────────────────────────────────────────
  // 터치 디바이스에서만 생성한다. 이동 자체는 update()의 키보드 이동 경로를
  // 그대로 타므로 (충돌 판정·move 전송 동일) 별도 동기화 로직이 없다.

  private static isTouchDevice(): boolean {
    return window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
  }

  private buildJoystick() {
    if (!this.isTouchUi) return;

    // scrollFactor 0 오브젝트는 카메라 줌(2x) 중심 스케일로 그려지므로
    // 화면 픽셀 크기를 줌으로 나눠서 지정한다 (positionJoystick의 좌표 변환과 동일 이유).
    const zoom = this.cameras.main.zoom;
    const base = this.add.circle(0, 0, JOY_BASE_RADIUS / zoom, 0xffffff, 0.15)
      .setStrokeStyle(2 / zoom, 0xffffff, 0.4).setDepth(500000);
    const thumb = this.add.circle(0, 0, JOY_THUMB_RADIUS / zoom, 0xffffff, 0.45)
      .setDepth(500001);
    this.joystick = new VirtualJoystick(this, {
      x: 0, y: 0, radius: JOY_BASE_RADIUS / zoom, base, thumb, // fixed: true (기본값)
    });
    this.positionJoystick();

    // 재계산 트리거 — 최초 생성/PC 리사이즈/모바일 회전 전부 같은
    // positionJoystick() 하나로 수렴한다.
    // ① Phaser resize: 이벤트 시점의 카메라 크기가 아직 회전 전 값일 수 있어
    //    (진단: 회전 시 stale width/height 관측) 다음 프레임에 재계산.
    this.scale.on("resize", this.scheduleJoystickLayout, this);
    // ② 모바일 회전: ScaleManager가 회전 전 부모 크기를 읽어 한 박자 늦는다 —
    //    orientationchange/visualViewport를 보조 트리거로 다음 프레임에
    //    scale.refresh()로 부모 크기를 재측정시킨다 (크기가 실제로 변했으면
    //    ①의 resize 이벤트가 이어서 발생 → 재계산. 중복 호출은 멱등).
    this.viewportChangeHandler = () => requestAnimationFrame(() => {
      if (!this.joystick) return; // 씬 재시작 직후 잔여 rAF 가드
      this.scale.refresh();
      this.scheduleJoystickLayout();
    });
    window.addEventListener("orientationchange", this.viewportChangeHandler);
    window.visualViewport?.addEventListener("resize", this.viewportChangeHandler);
  }

  // 겹치는 트리거(resize+orientationchange+visualViewport)를 다음 프레임 1회로 합친다
  private scheduleJoystickLayout() {
    if (this.joyLayoutPending) return;
    this.joyLayoutPending = true;
    requestAnimationFrame(() => {
      this.joyLayoutPending = false;
      this.positionJoystick();
    });
  }

  // 화면 좌하단 고정 — 항상 호출 시점의 카메라 크기 기준 상대 위치(여백 고정)로
  // 계산하므로 어떤 해상도/방향에서도 같은 코드로 배치된다. 줌 z에서 scrollFactor 0
  // 오브젝트의 화면 좌표는 screen = (pos - center) * z + center 이므로 역변환해 배치.
  private positionJoystick() {
    if (!this.joystick) return;
    const cam = this.cameras.main;
    const zoom = cam.zoom;
    const sx = JOY_MARGIN_X;
    const sy = cam.height - JOY_BOTTOM_MARGIN;
    const cx = cam.width / 2;
    const cy = cam.height / 2;
    this.joystick.setPosition((sx - cx) / zoom + cx, (sy - cy) / zoom + cy);
  }

  private doHunt() {
    if (!this.room || !this.huntTargetId) return;
    this.room.send("hunt", {});
  }

  // Floating combat text (damage numbers, miss indicator)
  private floatText(x: number, y: number, text: string, color: string) {
    const t = this.add.text(x, y - 10, text, {
      fontSize: "7px", color, stroke: "#000000", strokeThickness: 2,
    }).setOrigin(0.5, 1).setDepth(100000).setResolution(TEXT_RESOLUTION);
    this.tweens.add({
      targets: t, y: y - 26, alpha: 0,
      duration: 650, onComplete: () => t.destroy(),
    });
  }

  // ─── Chat UI ─────────────────────────────────────────────────────────────

  private buildChatUI() {
    const wrap = document.createElement("div");
    wrap.style.cssText = [
      "position:fixed;bottom:0;left:0;width:100%;height:150px",
      "background:rgba(0,0,0,0.65);display:flex;flex-direction:column",
      "padding:6px 8px;box-sizing:border-box;z-index:100;gap:4px",
    ].join(";");

    this.chatBox = document.createElement("div");
    this.chatBox.style.cssText = [
      "flex:1;overflow-y:auto;font:12px/1.4 monospace",
      "color:#e0e0e0;word-break:break-all",
    ].join(";");

    const inputRow = document.createElement("div");
    inputRow.style.cssText = "display:flex;gap:6px";

    this.chatInput = document.createElement("input");
    this.chatInput.placeholder = "메시지 입력 후 Enter";
    this.chatInput.maxLength = 200;
    this.chatInput.style.cssText = [
      "flex:1;background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.25)",
      "color:#fff;padding:3px 8px;font-size:12px;outline:none;border-radius:4px",
    ].join(";");

    const btn = document.createElement("button");
    btn.textContent = "전송";
    btn.style.cssText = [
      "background:#3d8b4e;border:none;color:#fff",
      "padding:3px 10px;cursor:pointer;border-radius:4px;font-size:12px",
    ].join(";");

    const send = () => {
      const text = this.chatInput.value.trim();
      if (text && this.room) {
        this.room.send("chat", { text });
        this.chatInput.value = "";
      }
    };

    this.chatInput.addEventListener("keydown", (e) => {
      // stopPropagation은 IME 조합 중에도 항상 — window의 Phaser 키 리스너로
      // 새어 나가면 채팅 타이핑(WASD)이 캐릭터를 움직인다.
      e.stopPropagation();
      // 한글 IME 조합 확정용 Enter(keydown이 한 번 더 발생, keyCode 229)를
      // 걸러내지 않으면 마지막 글자가 한 번 더 전송된다.
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === "Enter") send();
    });
    btn.addEventListener("click", send);

    // Pause keyboard capture while typing
    this.chatInput.addEventListener("focus", () => this.input.keyboard?.disableGlobalCapture());
    this.chatInput.addEventListener("blur",  () => this.input.keyboard?.enableGlobalCapture());

    inputRow.append(this.chatInput, btn);
    wrap.append(this.chatBox, inputRow);
    this.chatWrap = wrap;
    document.body.appendChild(this.trackDom(wrap));

    // 모바일: 기본 접힘 + 💬 토글 버튼(우하단) + 안 읽은 배지 + 미리보기 토스트.
    // PC는 기존 그대로 항상 펼침.
    if (this.isTouchUi) {
      this.chatOpen = false;
      wrap.style.display = "none";
      // 펼침 시 backdrop(117) 위로 — 접힘/PC 기본값은 100 (사이드 패널 118 아래)
      this.buildChatToggle();
    }
  }

  private buildChatToggle() {
    this.chatToggleBtn = document.createElement("button");
    this.chatToggleBtn.title = "채팅";
    this.chatToggleBtn.style.cssText = [
      "position:fixed;right:12px;bottom:12px;z-index:118;width:48px;height:48px",
      "background:rgba(20,20,40,0.85);border:1px solid rgba(255,255,255,0.3)",
      "border-radius:50%;font-size:20px;cursor:pointer;color:#fff",
    ].join(";");
    this.chatToggleBtn.textContent = "💬";

    this.chatBadge = document.createElement("span");
    this.chatBadge.style.cssText = [
      "position:absolute;top:-5px;right:-5px;min-width:18px;height:18px;display:none",
      "background:#e74c3c;color:#fff;border-radius:9px;font:700 11px/18px monospace",
      "padding:0 4px;box-sizing:border-box;text-align:center",
    ].join(";");
    this.chatToggleBtn.appendChild(this.chatBadge);
    this.chatToggleBtn.addEventListener("click", () => this.toggleChat());
    document.body.appendChild(this.trackDom(this.chatToggleBtn));

    // 접힘 중 최신 메시지 1줄 미리보기 (3초 후 소멸 — 광장 생동감 유지)
    this.chatPreview = document.createElement("div");
    this.chatPreview.style.cssText = [
      "position:fixed;right:12px;bottom:68px;z-index:116;max-width:66vw;display:none",
      "background:rgba(0,0,0,0.75);border:1px solid rgba(255,255,255,0.25);color:#e0e0e0",
      "border-radius:8px;padding:5px 10px;font:11px/1.4 monospace;pointer-events:none",
      "overflow:hidden;text-overflow:ellipsis;white-space:nowrap",
    ].join(";");
    document.body.appendChild(this.trackDom(this.chatPreview));
  }

  private toggleChat() {
    if (!this.isTouchUi) return;
    this.setChatOpen(!this.chatOpen);
  }

  private setChatOpen(open: boolean) {
    if (!this.isTouchUi || this.chatOpen === open) return;
    this.chatOpen = open;
    if (open) {
      this.closeSidePanels(); // 채팅과 사이드 팝업은 동시에 열지 않는다
      this.chatWrap.style.display = "flex";
      this.chatWrap.style.zIndex = "130"; // backdrop(117) 위
      this.chatUnread = 0;
      this.updateChatBadge();
      this.chatPreview.style.display = "none";
      this.chatBox.scrollTop = this.chatBox.scrollHeight;
    } else {
      this.chatWrap.style.display = "none";
      this.chatWrap.style.zIndex = "100";
      this.chatInput.blur();
    }
    this.syncModalBackdrop();
  }

  private updateChatBadge() {
    if (!this.isTouchUi) return;
    this.chatBadge.style.display = this.chatUnread > 0 ? "block" : "none";
    this.chatBadge.textContent = this.chatUnread > 9 ? "9+" : String(this.chatUnread);
  }

  private showChatPreview(nickname: string, text: string) {
    this.chatPreview.textContent = `[${nickname}] ${text}`;
    this.chatPreview.style.display = "block";
    this.chatPreviewTimer?.remove();
    this.chatPreviewTimer = this.time.delayedCall(3000, () => {
      this.chatPreview.style.display = "none";
    });
  }

  // ─── Battle log (above chat) ───────────────────────────────────────────────

  private buildBattleLog() {
    this.battleLog = document.createElement("div");
    if (this.isTouchUi) {
      // 모바일: 채팅이 접히면서 하단 띠가 사라졌으므로, 전투 로그는 화면을 덜
      // 가리는 일시 표시로 — 배경 없는 오버레이에 블록이 잠깐 떴다 사라진다.
      // 조이스틱(좌하단, 중심 h-120·반경 52) 위쪽에 배치, 터치는 통과시킨다.
      this.battleLog.style.cssText = [
        "position:fixed;bottom:190px;left:12px;width:min(70vw,320px);z-index:99",
        "font:11px/1.4 monospace;color:#ffd7d7;text-shadow:0 1px 2px rgba(0,0,0,0.9)",
        "pointer-events:none",
      ].join(";");
    } else {
      this.battleLog.style.cssText = [
        "position:fixed;bottom:150px;left:0;width:100%;height:104px;z-index:99",
        "background:rgba(20,10,10,0.6);border-top:1px solid rgba(255,120,120,0.25)",
        "overflow-y:auto;font:12px/1.4 monospace;color:#f0d0d0",
        "padding:6px 8px;box-sizing:border-box",
      ].join(";");
    }
    document.body.appendChild(this.trackDom(this.battleLog));
  }

  private pushBattleLog(lines: string[]) {
    const block = document.createElement("div");
    block.style.cssText = "margin-bottom:6px;padding-bottom:6px;border-bottom:1px solid rgba(255,255,255,0.08)";
    for (const line of lines) {
      const row = document.createElement("div");
      row.textContent = line;
      block.appendChild(row);
    }
    this.battleLog.appendChild(block);
    if (this.isTouchUi) {
      // 모바일: 최근 3블록만 유지하고 각 블록은 4초 후 사라진다
      while (this.battleLog.children.length > 3) {
        this.battleLog.removeChild(this.battleLog.firstChild!);
      }
      this.time.delayedCall(4000, () => {
        block.style.transition = "opacity 0.4s";
        block.style.opacity = "0";
        this.time.delayedCall(400, () => block.remove());
      });
      return;
    }
    // keep max 20 exchanges
    while (this.battleLog.children.length > 20) {
      this.battleLog.removeChild(this.battleLog.firstChild!);
    }
    this.battleLog.scrollTop = this.battleLog.scrollHeight;
  }

  // ─── Hunt prompt ("사냥하기") ────────────────────────────────────────────

  private buildHuntPrompt() {
    this.huntPrompt = document.createElement("div");
    this.huntPrompt.textContent = "⚔ [E] 사냥하기";
    this.huntPrompt.style.cssText = [
      "position:fixed;bottom:264px;left:50%;transform:translateX(-50%);z-index:115",
      "background:rgba(0,0,0,0.75);border:1px solid #f6c945;color:#f6c945",
      "padding:8px 18px;border-radius:20px;font:700 13px monospace;cursor:pointer",
      "display:none",
    ].join(";");
    this.huntPrompt.addEventListener("click", () => this.doHunt());
    document.body.appendChild(this.trackDom(this.huntPrompt));
  }

  // Nearest alive monster within hunt range — updates the prompt visibility
  private checkHuntTarget() {
    if (!this.room?.state) return;
    const px = this.localPlayer.x;
    const py = this.localPlayer.y;
    let bestId: string | null = null;
    let best: number = COMBAT.HUNT_RANGE;

    this.room.state.monsters.forEach((m: any, id: string) => {
      if (!m.alive) return;
      const d = Phaser.Math.Distance.Between(px, py, m.x, m.y);
      if (d <= best) { best = d; bestId = id; }
    });

    this.huntTargetId = bestId;
    this.huntPrompt.style.display = bestId ? "block" : "none";
  }

  // ─── HUD (level / exp) ───────────────────────────────────────────────────

  private buildHud() {
    const wrap = document.createElement("div");
    wrap.style.cssText = [
      "position:fixed;top:12px;left:12px;z-index:110;width:220px",
      "background:rgba(0,0,0,0.6);border:1px solid rgba(255,255,255,0.2)",
      "border-radius:8px;padding:8px 10px;font-family:monospace;color:#fff",
    ].join(";");

    this.hudLevel = document.createElement("div");
    this.hudLevel.style.cssText = "font-size:14px;font-weight:700;margin-bottom:6px";
    wrap.appendChild(this.hudLevel);

    const hp      = this.buildHudGauge("❤️", "#e74c3c");
    const stamina = this.buildHudGauge("⚡", "#3498db");
    const exp     = this.buildHudGauge("⭐", "#f6c945");

    this.hudHpFill      = hp.fill;      this.hudHpText      = hp.text;
    this.hudStaminaFill = stamina.fill; this.hudStaminaText = stamina.text;
    this.hudExpFill     = exp.fill;     this.hudExpText     = exp.text;

    wrap.append(hp.row, stamina.row, exp.row);

    const goldRow = document.createElement("div");
    goldRow.style.cssText = "display:flex;align-items:center;gap:5px;margin-top:2px";
    const goldIcon = document.createElement("div");
    goldIcon.textContent = "💰";
    goldIcon.style.cssText = "font-size:12px;width:14px;flex-shrink:0;text-align:center";
    this.hudGoldText = document.createElement("div");
    this.hudGoldText.style.cssText = "font-size:12px;font-weight:700;color:#f6c945";
    goldRow.append(goldIcon, this.hudGoldText);
    wrap.appendChild(goldRow);

    document.body.appendChild(this.trackDom(wrap));

    this.updateHud(1, 0);
    this.updateStaminaHud(100, 100);
    this.updateHpHud(100, 100);
    this.updateGoldHud(0);
  }

  // One gauge row: icon + bar (colored fill, tweened) with a "current/max"
  // number overlay centered on top of the bar.
  private buildHudGauge(icon: string, color: string): {
    row: HTMLDivElement; fill: HTMLDivElement; text: HTMLDivElement;
  } {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;align-items:center;gap:5px;margin-bottom:5px";

    const iconEl = document.createElement("div");
    iconEl.textContent = icon;
    iconEl.style.cssText = "font-size:12px;width:14px;flex-shrink:0;text-align:center";

    const bar = document.createElement("div");
    bar.style.cssText = [
      "position:relative;flex:1;height:14px;background:rgba(255,255,255,0.12)",
      "border-radius:7px;overflow:hidden",
    ].join(";");

    const fill = document.createElement("div");
    fill.style.cssText = [
      `position:absolute;inset:0;width:0%;background:${color}`,
      "transition:width 0.3s ease",
    ].join(";");

    const text = document.createElement("div");
    text.style.cssText = [
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center",
      "font-size:9px;color:#fff;text-shadow:0 1px 1px #000",
    ].join(";");

    bar.append(fill, text);
    row.append(iconEl, bar);
    return { row, fill, text };
  }

  private updateHud(level: number, exp: number) {
    const need = expForLevel(level);
    const pct  = Math.min(100, Math.round((exp / need) * 100));
    this.lastLevel = level;
    this.lastExp   = exp;
    // 직급은 레벨 옆에 함께 표시 ("Lv. 4 주니어")
    this.hudLevel.textContent   = this.rank ? `Lv. ${level} ${this.rank}` : `Lv. ${level}`;
    this.hudExpFill.style.width = `${pct}%`;
    this.hudExpText.textContent = `${exp}/${need}`;
  }

  private updateStaminaHud(value: number, max: number) {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    this.hudStaminaFill.style.width = `${pct}%`;
    this.hudStaminaText.textContent = `${value}/${max}`;
  }

  private updateHpHud(hp: number, maxHp: number) {
    const pct = maxHp > 0 ? Math.min(100, Math.round((hp / maxHp) * 100)) : 0;
    this.hudHpFill.style.width = `${pct}%`;
    this.hudHpText.textContent = `${hp}/${maxHp}`;
  }

  private updateGoldHud(value: number) {
    this.hudGoldText.textContent = `${value}G`;
  }

  // ─── Quest tracker ───────────────────────────────────────────────────────

  private buildQuestTracker() {
    // 좌측 상단 HUD 게이지(레벨/HP/집중력/EXP/골드, ~높이 150px) 바로 아래 —
    // 게이지나 우측 버튼 스택과 겹치지 않는다.
    this.questTracker = document.createElement("div");
    this.questTracker.style.cssText = [
      "position:fixed;top:170px;left:12px;z-index:110;width:220px",
      "background:rgba(0,0,0,0.55);border:1px solid rgba(155,89,182,0.6)",
      "border-radius:8px;padding:8px 10px;font:11px/1.5 monospace;color:#fff;display:none",
    ].join(";");
    document.body.appendChild(this.trackDom(this.questTracker));
  }

  // ─── 일일 버그 리포트 트래커 ─────────────────────────────────────────────
  // 튜토리얼 트래커와 같은 좌측 자리를 쓴다 — 데일리는 튜토리얼 완료자에게만
  // 오므로 두 트래커가 동시에 보일 일은 없다.

  private buildDailyTracker() {
    // 접힘 상태: 📋 배지 버튼 (데일리 상태가 처음 도착할 때까지 숨김)
    this.dailyBadgeBtn = document.createElement("button");
    this.dailyBadgeBtn.title = "오늘의 할 일";
    this.dailyBadgeBtn.style.cssText = [
      "position:fixed;top:170px;left:12px;z-index:118;width:44px;height:44px;display:none",
      "background:rgba(0,0,0,0.6);border:1px solid rgba(93,173,226,0.6)",
      "border-radius:10px;font-size:19px;cursor:pointer;color:#fff",
    ].join(";");
    this.dailyBadgeBtn.textContent = "📋";

    this.dailyBadge = document.createElement("span");
    this.dailyBadge.style.cssText = [
      "position:absolute;top:-6px;right:-6px;min-width:18px;height:18px;display:none",
      "color:#fff;border-radius:9px;font:700 11px/18px monospace",
      "padding:0 4px;box-sizing:border-box;text-align:center",
    ].join(";");
    this.dailyBadgeBtn.appendChild(this.dailyBadge);
    this.dailyBadgeBtn.addEventListener("click", () => this.togglePanel("daily"));
    document.body.appendChild(this.trackDom(this.dailyBadgeBtn));

    // 펼침 상태: 기존 트래커 패널 (공통 모달 로직이 열고 닫는다)
    this.dailyTracker = document.createElement("div");
    this.dailyTracker.style.cssText = [
      "position:fixed;top:170px;left:12px;z-index:118;width:220px",
      "background:rgba(0,0,0,0.85);border:1px solid rgba(93,173,226,0.6)",
      "border-radius:8px;padding:8px 10px;font:11px/1.6 monospace;color:#fff;display:none",
    ].join(";");
    document.body.appendChild(this.trackDom(this.dailyTracker));
  }

  // 배지: 남은 할 일 개수(빨강) → 보상 수령 대기 🎁(노랑) → 올클리어 ✓(초록)
  private updateDailyBadge() {
    const state = this.dailyState;
    if (!state || !state.quests.length) {
      this.dailyBadgeBtn.style.display = "none";
      if (this.openPanel === "daily") this.closeSidePanels();
      return;
    }
    this.dailyBadgeBtn.style.display = "block";
    const remaining = state.quests.filter((q) => q.progress < q.target).length;
    this.dailyBadge.style.display = "block";
    if (remaining > 0) {
      this.dailyBadge.style.background = "#e74c3c";
      this.dailyBadge.textContent = String(remaining);
    } else if (!state.allClaimed) {
      this.dailyBadge.style.background = "#b07d1a";
      this.dailyBadge.textContent = "🎁";
    } else {
      this.dailyBadge.style.background = "#27ae60";
      this.dailyBadge.textContent = "✓";
    }
  }

  private renderDailyTracker() {
    const state = this.dailyState;
    if (!state || !state.quests.length) return;
    const lines = state.quests.map((q) => {
      const icon = q.claimed ? "✅" : q.progress >= q.target ? "🎁" : "□";
      return `<div>${icon} ${escapeHtml(q.label)} (${q.progress}/${q.target})</div>`;
    });
    this.dailyTracker.innerHTML =
      `<div style="color:#5dade2;font-weight:700;margin-bottom:2px">📋 오늘의 할 일</div>` +
      lines.join("") +
      (state.allClaimed
        ? `<div style="color:#f6c945">오늘 몫 끝! 내일 또 만나요.</div>`
        : state.quests.some((q) => !q.claimed && q.progress >= q.target)
          ? `<div style="color:#f6c945">🎁 대시보드에서 보상을 받으세요</div>`
          : "");
  }

  private findActiveTutorialStep() {
    return TUTORIAL_STEPS.find((s) => this.quests[s.id]?.status === "active");
  }

  // Diffs against the previous active step so a step that just completed
  // (including the last one) gets a toast, without the server needing to
  // send a separate per-step "update" message.
  private applyQuestState(newQuests: QuestMap) {
    const prevActive = this.findActiveTutorialStep();
    this.quests = newQuests;
    const nowActive = this.findActiveTutorialStep();
    if (prevActive && prevActive.id !== nowActive?.id) {
      this.showToast(`✅ 퀘스트 완료: ${prevActive.title}`, "#2ecc71", 2200);
      this.pushChat("System", `퀘스트 '${prevActive.title}' 완료!`, true);
    }
    this.refreshQuestTracker();
  }

  private refreshQuestTracker() {
    const step = this.findActiveTutorialStep();
    if (!step) {
      this.questTracker.style.display = "none";
      return;
    }
    this.questTracker.style.display = "block";
    this.questTracker.innerHTML =
      `<div style="color:#c39bd8;font-weight:700;margin-bottom:2px">[튜토리얼] ${escapeHtml(step.title)}</div>` +
      escapeHtml(step.hint);
  }

  // ─── Dialogue ────────────────────────────────────────────────────────────

  private buildDialogueBox() {
    // 모바일: 조이스틱(좌하단, 상단 가장자리가 화면 하단에서 172px)을 덮지
    // 않도록 대화창을 그 위로 올린다. PC는 채팅 띠(150px) 바로 위.
    const bottom = this.isTouchUi ? 185 : 165;
    this.dialogueBox = document.createElement("div");
    this.dialogueBox.style.cssText = [
      `position:fixed;left:50%;bottom:${bottom}px;transform:translateX(-50%);z-index:130`,
      "width:min(560px,90vw);background:rgba(0,0,0,0.82);border:1px solid rgba(255,255,255,0.3)",
      "border-radius:10px;padding:16px 20px;font-family:monospace;color:#fff;display:none;cursor:pointer",
    ].join(";");

    this.dialogueName = document.createElement("div");
    this.dialogueName.style.cssText = "color:#c39bd8;font-weight:700;font-size:13px;margin-bottom:8px";

    this.dialogueText = document.createElement("div");
    this.dialogueText.style.cssText = "font-size:14px;line-height:1.6;min-height:22px";

    const hint = document.createElement("div");
    hint.textContent = "▶ 클릭해서 계속하기";
    hint.style.cssText = "color:rgba(255,255,255,0.45);font-size:11px;margin-top:10px;text-align:right";

    this.dialogueBox.append(this.dialogueName, this.dialogueText, hint);
    this.dialogueBox.addEventListener("click", () => this.advanceDialogue());
    document.body.appendChild(this.trackDom(this.dialogueBox));
  }

  private openDialogue(npc: NpcDef) {
    this.activeDialogue = { npc, index: 0 };
    this.dialogueName.textContent = npc.label;
    this.dialogueText.textContent = npc.lines[0] ?? "";
    this.dialogueBox.style.display = "block";
  }

  private advanceDialogue() {
    if (!this.activeDialogue) return;
    const { npc } = this.activeDialogue;
    const next = this.activeDialogue.index + 1;
    if (next < npc.lines.length) {
      this.activeDialogue.index = next;
      this.dialogueText.textContent = npc.lines[next];
    } else {
      // Finished — don't reopen while the player is still standing in range
      this.dismissedNpcs.add(npc.id);
      this.closeDialogue();
      if (npc.id === "git_master") this.sendTutorialAdvance(FIRST_TUTORIAL_STEP_ID);
      if (npc.id === "hr_manager") void this.openExamModal();
    }
  }

  private closeDialogue() {
    this.activeDialogue = null;
    this.dialogueBox.style.display = "none";
  }

  private sendTutorialAdvance(stepId: string) {
    this.room?.send("tutorial_advance", { stepId });
  }

  // ─── Inventory ───────────────────────────────────────────────────────────

  private buildInventory() {
    // I 단축키와 동일한 토글 — 버튼으로도 열 수 있게 한다.
    this.buildSideButton("🎒 인벤토리", 232, () => this.toggleInventory());

    this.inventoryPanel = document.createElement("div");
    this.inventoryPanel.style.cssText = this.sidePanelCss();

    const header = document.createElement("div");
    header.textContent = "🎒 인벤토리 (I)";
    header.style.cssText = [
      "font-size:14px;font-weight:700;margin-bottom:10px",
      "border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:8px",
    ].join(";");

    this.inventoryList = document.createElement("div");
    this.inventoryList.style.cssText = "display:flex;flex-direction:column;gap:8px";

    this.inventoryPanel.append(header, this.inventoryList);
    document.body.appendChild(this.trackDom(this.inventoryPanel));

    // Tooltip (shared)
    this.tooltip = document.createElement("div");
    this.tooltip.style.cssText = [
      "position:fixed;z-index:140;max-width:240px;background:rgba(0,0,0,0.92)",
      "border:1px solid rgba(255,255,255,0.3);border-radius:8px;padding:10px 12px",
      "font-family:monospace;color:#fff;pointer-events:none;display:none",
    ].join(";");
    document.body.appendChild(this.trackDom(this.tooltip));
  }

  private toggleInventory() {
    this.togglePanel("inventory");
  }

  private async loadInventory() {
    try {
      const res = await apiFetch(`${API_URL}/items/inventory`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const data = await res.json() as { items: InvItem[] };
      this.renderInventory(data.items);
    } catch { /* ignore */ }
  }

  private renderInventory(items: InvItem[]) {
    this.inventoryList.innerHTML = "";
    if (!items.length) {
      const empty = document.createElement("div");
      empty.textContent = "아이템이 없습니다.";
      empty.style.cssText = "color:rgba(255,255,255,0.4);font-size:12px";
      this.inventoryList.appendChild(empty);
      return;
    }

    for (const item of items) {
      const row = document.createElement("div");
      row.style.cssText = [
        "display:flex;align-items:center;gap:10px;padding:8px;border-radius:8px",
        "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12);cursor:pointer",
      ].join(";");
      row.onmouseenter = () => { row.style.background = "rgba(255,255,255,0.12)"; };
      row.onmouseleave = () => { row.style.background = "rgba(255,255,255,0.05)"; };

      const icon = document.createElement("div");
      icon.textContent = item.icon;
      icon.style.cssText = "font-size:24px";

      const info = document.createElement("div");
      info.style.cssText = "flex:1;min-width:0";
      const nm = document.createElement("div");
      nm.textContent = item.name;
      nm.style.cssText = "font-size:12px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis";
      const qty = document.createElement("div");
      qty.textContent = `x${item.quantity}`;
      qty.style.cssText = "font-size:11px;color:rgba(255,255,255,0.5)";
      info.append(nm, qty);

      row.append(icon, info);

      // Tooltip on hover
      row.addEventListener("mousemove", (e) => this.showTooltip(item, e.clientX, e.clientY));
      row.addEventListener("mouseleave", () => this.hideTooltip());
      // Click to use
      row.addEventListener("click", () => {
        this.room?.send("use_item", { itemId: item.id });
        this.hideTooltip();
      });

      this.inventoryList.appendChild(row);
    }
  }

  private showTooltip(item: InvItem, cx: number, cy: number) {
    this.tooltip.innerHTML =
      `<div style="font-size:13px;font-weight:700;margin-bottom:4px">${item.icon} ${escapeHtml(item.name)}</div>` +
      `<div style="font-size:11px;line-height:1.5;color:rgba(255,255,255,0.8);margin-bottom:6px">${escapeHtml(item.description)}</div>` +
      `<div style="font-size:11px;color:#f6c945">▸ ${escapeHtml(effectSummary(item.effect))}</div>`;
    this.tooltip.style.display = "block";
    // Keep on-screen (panel is on the right, so anchor tooltip to the left of cursor)
    const tw = 240;
    this.tooltip.style.left = `${Math.max(8, cx - tw - 12)}px`;
    this.tooltip.style.top  = `${Math.min(window.innerHeight - 90, cy)}px`;
  }

  private hideTooltip() {
    this.tooltip.style.display = "none";
  }

  private applyItemUsed(data: {
    ok: boolean; itemId?: string; remaining?: number; reason?: "no_item" | "daily_limit";
    buff?: { multiplier: number; durationMs: number }; staminaGained?: number;
  }) {
    if (!data.ok) {
      if (data.reason === "daily_limit") {
        this.showToast("오늘은 더 못 마셔. 위장이 파업 중이야.", "#e74c3c", 1800);
      }
      return;
    }
    if (data.buff) {
      this.speedMultiplier = data.buff.multiplier;
      this.speedBuffUntil  = this.time.now + data.buff.durationMs;
      this.showToast(`☕ 이동속도 +${Math.round((data.buff.multiplier - 1) * 100)}%!`, "#8e6d3f", 1500);
    } else if (data.staminaGained) {
      this.showToast(`☕ 집중력 +${data.staminaGained} 회복!`, "#3498db", 1500);
    } else {
      this.showToast("아이템 사용", "#2ecc71", 1000);
    }
    if (this.inventoryOpen) void this.loadInventory();
  }

  // ─── 우측 사이드 버튼/패널 공통 ─────────────────────────────────────────

  // 우측 상단 버튼 스타일 통일 — 같은 폭/폰트/간격, danger만 색 강조.
  private buildSideButton(
    label: string, top: number, onClick: () => void, opts: { danger?: boolean } = {}
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.textContent = label;
    btn.style.cssText = [
      `position:fixed;top:${top}px;right:12px;z-index:118;width:116px;text-align:center`,
      opts.danger
        ? "background:rgba(200,60,60,0.85);border:1px solid rgba(255,255,255,0.25)"
        : "background:rgba(60,60,110,0.85);border:1px solid rgba(255,255,255,0.25)",
      "color:#fff;padding:7px 0;cursor:pointer;border-radius:6px",
      "font:600 13px monospace;transition:opacity 0.15s",
    ].join(";");
    btn.onmouseenter = () => { btn.style.opacity = "0.8"; };
    btn.onmouseleave = () => { btn.style.opacity = "1"; };
    btn.addEventListener("click", onClick);
    document.body.appendChild(this.trackDom(btn));
    return btn;
  }

  // 사이드 패널 공통 스타일 — 버튼 스택(로그아웃~인벤토리) 아래에 열린다.
  private sidePanelCss(): string {
    return [
      "position:fixed;top:280px;right:12px;z-index:118;width:280px",
      "max-height:min(55vh,460px)",
      "background:rgba(20,20,40,0.94);border:1px solid rgba(255,255,255,0.2);border-radius:10px",
      "padding:12px 14px;box-sizing:border-box;font-family:monospace;color:#fff",
      "overflow-y:auto;display:none",
    ].join(";");
  }

  // ─── 공통 모달 유틸 ──────────────────────────────────────────────────────
  // 사이드 팝업 전부(우측 5종 + 좌측 데일리)는 togglePanel/closeSidePanels
  // 하나의 경로로만 열고 닫는다: 동시에 하나만 열림, 열림 중 backdrop 표시
  // (바깥 탭 = 닫기), 캔버스 이동 차단은 update()의 movementBlocked()가 본다.

  private sidePanelDefs(): Record<SidePanelName, {
    el: HTMLElement; onOpen?: () => void; onClose?: () => void;
  }> {
    return {
      project: { el: this.projectPanel, onOpen: () => void this.loadProjectMarket() },
      skill: { el: this.skillPanel, onOpen: () => void this.loadSkills() },
      travel: { el: this.travelPanel, onOpen: () => this.renderTravelPanel() },
      codex: { el: this.codexPanel, onOpen: () => void this.loadCodex() },
      inventory: {
        el: this.inventoryPanel,
        onOpen: () => void this.loadInventory(),
        onClose: () => this.hideTooltip(),
      },
      daily: { el: this.dailyTracker, onOpen: () => this.renderDailyTracker() },
    };
  }

  private togglePanel(name: SidePanelName) {
    const willOpen = this.openPanel !== name;
    this.setChatOpen(false); // 모바일 채팅과 사이드 팝업은 동시에 열지 않는다
    this.closeSidePanels();
    if (willOpen) {
      const def = this.sidePanelDefs()[name];
      this.openPanel = name;
      def.el.style.display = "block";
      def.onOpen?.();
    }
    this.syncModalBackdrop();
  }

  private closeSidePanels() {
    if (this.openPanel === null) return;
    const defs = this.sidePanelDefs();
    for (const name of Object.keys(defs) as SidePanelName[]) {
      defs[name].el.style.display = "none";
      defs[name].onClose?.();
    }
    this.openPanel = null;
    this.syncModalBackdrop();
  }

  private buildModalBackdrop() {
    this.modalBackdrop = document.createElement("div");
    // z-index: 채팅(100)·HUD(110) 위, 팝업·사이드 버튼(118) 아래 — 버튼은
    // backdrop 위에 남아 다른 팝업으로 바로 전환할 수 있다.
    this.modalBackdrop.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,0.35);z-index:117;display:none";
    this.modalBackdrop.addEventListener("click", () => this.closeAllPopups());
    document.body.appendChild(this.trackDom(this.modalBackdrop));
  }

  private syncModalBackdrop() {
    const show = this.openPanel !== null || (this.isTouchUi && this.chatOpen);
    this.modalBackdrop.style.display = show ? "block" : "none";
  }

  // backdrop(팝업 바깥) 탭 — 열려 있는 사이드 팝업/모바일 채팅을 모두 닫는다
  private closeAllPopups() {
    this.closeSidePanels();
    this.setChatOpen(false);
  }

  // ─── Freelance projects ────────────────────────────────────────────────

  private buildProjectPanel() {
    this.buildSideButton("📋 프로젝트", 56, () => this.toggleProjectPanel());

    this.projectPanel = document.createElement("div");
    this.projectPanel.style.cssText = this.sidePanelCss();

    this.projectPanelHeader = document.createElement("div");
    this.projectPanelHeader.textContent = "📋 프리랜서 프로젝트";
    this.projectPanelHeader.style.cssText = [
      "font-size:14px;font-weight:700;margin-bottom:10px",
      "border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:8px",
    ].join(";");

    this.projectPanelBody = document.createElement("div");

    this.projectPanel.append(this.projectPanelHeader, this.projectPanelBody);
    document.body.appendChild(this.trackDom(this.projectPanel));

    this.time.addEvent({ delay: 1000, loop: true, callback: () => this.tickProjectCountdown() });
  }

  private toggleProjectPanel() {
    this.togglePanel("project");
  }

  private async loadProjectMarket() {
    try {
      const [marketRes, statusRes] = await Promise.all([
        apiFetch(`${API_URL}/projects/market`),
        apiFetch(`${API_URL}/companies/status`),
      ]);
      if (marketRes.ok) {
        const data = await marketRes.json() as {
          main: ActiveProjectView | null; side: ActiveProjectView | null; market: ProjectTemplate[];
        };
        this.projectMarket = data.market;
        this.applyProjectStates(data.main, data.side);
      }
      if (statusRes.ok) {
        this.employment = await statusRes.json() as EmploymentStatus;
        if (this.projectPanelOpen) this.renderProjectPanel();
      }
    } catch { /* ignore */ }
  }

  private async applyForCompany(companyId: number) {
    try {
      const res = await apiFetch(`${API_URL}/companies/apply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId }),
      });
      const data = await res.json() as {
        companyName?: string; changed?: boolean; fromCompanyName?: string; error?: string;
      };
      if (!res.ok || !data.companyName) {
        this.showToast(data.error ?? "지원할 수 없습니다", "#e74c3c", 2200);
        return;
      }
      if (data.changed) {
        this.showToast(`🔀 "${data.companyName}"(으)로 이직했습니다!`, "#2ecc71", 2500);
        this.pushBattleLog([`🔀 "${data.fromCompanyName}"에 사직서를 던지고 "${data.companyName}"(으)로 이직했다.`]);
      } else {
        this.showToast(`🏢 "${data.companyName}"에 입사했습니다!`, "#2ecc71", 2200);
        this.pushBattleLog([`🏢 "${data.companyName}"에 입사했습니다.`]);
      }
      await this.loadProjectMarket();
    } catch {
      this.showToast("서버 연결 실패", "#e74c3c", 1800);
    }
  }

  private confirmResign() {
    this.showConfirm(
      "정말 퇴사하시겠습니까? 4대보험이 사라집니다.",
      () => void this.resignCompany()
    );
  }

  private async resignCompany() {
    try {
      const res = await apiFetch(`${API_URL}/companies/resign`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        this.showToast(data.error ?? "퇴사할 수 없습니다", "#e74c3c", 1800);
        return;
      }
      this.showToast("👋 퇴사했습니다. 이제 프리랜서입니다.", "#f6c945", 2200);
      this.pushBattleLog(["👋 퇴사했습니다. 프로젝트 마켓이 열렸습니다."]);
      await this.loadProjectMarket();
    } catch {
      this.showToast("서버 연결 실패", "#e74c3c", 1800);
    }
  }

  private async acceptProjectTemplate(templateId: number) {
    try {
      const res = await apiFetch(`${API_URL}/projects/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId }),
      });
      const data = await res.json() as { project?: ActiveProjectView; error?: string };
      if (!res.ok || !data.project) {
        this.showToast(data.error ?? "수주할 수 없습니다", "#e74c3c", 1800);
        return;
      }
      if (data.project.sideJob) {
        this.projectSide = data.project;
        this.projectTab = "side";
        this.pushBattleLog([`💼 부업 "${data.project.title}"을(를) 수주했습니다. 들키지 맙시다.`]);
      } else {
        this.projectMain = data.project;
        this.pushBattleLog([`📋 "${data.project.title}" 의뢰를 수주했습니다.`]);
      }
      this.updateProjectEventModal();
      if (this.projectPanelOpen) this.renderProjectPanel();
    } catch {
      this.showToast("서버 연결 실패", "#e74c3c", 1800);
    }
  }

  // 정규직의 부업 수주 — 취업규칙 경고를 거친다
  private confirmSideJob(templateId: number) {
    this.showConfirm(SIDE_JOB_WARNING, () => void this.acceptProjectTemplate(templateId));
  }

  private doProjectWork(slot: "main" | "side") {
    this.room?.send("project_work", { slot });
  }

  private doProjectChoice(slot: "main" | "side", choiceId: string) {
    this.room?.send("project_choice", { slot, choiceId });
  }

  // Applied from both the REST fetch (market/accept) and colyseus pushes —
  // single entry point so the modal + panel always reflect the latest state.
  private applyProjectStates(main: ActiveProjectView | null, side: ActiveProjectView | null) {
    this.projectMain = main;
    this.projectSide = side;
    this.updateProjectEventModal();
    if (this.projectPanelOpen) this.renderProjectPanel();
  }

  private renderProjectPanel() {
    this.projectPanelBody.innerHTML = "";
    this.projectDeadlineText = null;
    this.displayedProject = null;
    const employed = this.employment?.employed ?? false;
    this.projectPanelHeader.textContent = employed ? "🏢 회사 업무" : "📋 프리랜서 프로젝트";

    if (!employed) {
      // 프리랜서: 탭 없음 — 본업 슬롯 하나
      this.projectTab = "main";
      if (this.projectMain && this.projectMain.status === "active") {
        this.renderActiveProject(this.projectMain, "main");
      } else {
        this.renderProjectMarket(false);
      }
      this.renderCompanyList();
      return;
    }

    // 정규직: 회사 업무 / 부업 탭
    this.renderProjectTabs();
    if (this.projectTab === "main") {
      if (this.projectMain && this.projectMain.status === "active") {
        this.renderActiveProject(this.projectMain, "main");
      } else {
        this.renderCompanyIdle();
      }
      this.renderCompanyList();
    } else {
      if (this.projectSide && this.projectSide.status === "active") {
        this.renderActiveProject(this.projectSide, "side");
      } else {
        const warn = document.createElement("div");
        warn.textContent = "⚠️ 취업규칙 제7조: 겸업 금지. ...들키지만 않으면 됩니다.";
        warn.style.cssText = "font-size:10px;color:#e7a33c;line-height:1.5;margin-bottom:10px";
        this.projectPanelBody.appendChild(warn);
        this.renderProjectMarket(true);
      }
    }
  }

  private renderProjectTabs() {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;gap:6px;margin-bottom:10px";
    const tabs: { key: "main" | "side"; label: string }[] = [
      { key: "main", label: "🏢 회사 업무" },
      { key: "side", label: "💼 부업" },
    ];
    for (const t of tabs) {
      const btn = document.createElement("button");
      const active = this.projectTab === t.key;
      btn.textContent = t.label;
      btn.dataset.projectTab = t.key;
      btn.style.cssText = [
        "flex:1;padding:6px;border-radius:6px;font:700 12px monospace;cursor:pointer",
        active
          ? "background:#3d6bb0;color:#fff;border:1px solid #3d6bb0"
          : "background:rgba(255,255,255,0.06);color:rgba(255,255,255,0.6);border:1px solid rgba(255,255,255,0.18)",
      ].join(";");
      btn.addEventListener("click", () => {
        this.projectTab = t.key;
        this.renderProjectPanel();
      });
      row.appendChild(btn);
    }
    this.projectPanelBody.appendChild(row);
  }

  private renderActiveProject(view: ActiveProjectView, slot: "main" | "side") {
    this.displayedProject = view;
    const title = document.createElement("div");
    title.textContent = view.title;
    title.style.cssText = "font-size:13px;font-weight:700;color:#f6c945;margin-bottom:4px";

    const desc = document.createElement("div");
    desc.textContent = view.description;
    desc.style.cssText = "font-size:11px;line-height:1.5;color:rgba(255,255,255,0.75);margin-bottom:8px";

    const progressWrap = document.createElement("div");
    progressWrap.style.cssText = [
      "position:relative;height:14px;background:rgba(255,255,255,0.12)",
      "border-radius:7px;overflow:hidden;margin-bottom:6px",
    ].join(";");
    const pct = Math.min(100, Math.round((view.progress / view.workUnits) * 100));
    const progressFill = document.createElement("div");
    progressFill.style.cssText = [
      `position:absolute;inset:0;width:${pct}%`,
      "background:linear-gradient(90deg,#6c9bf0,#4a6fd6);transition:width 0.25s",
    ].join(";");
    const progressText = document.createElement("div");
    progressText.textContent = `진행도 ${view.progress}/${view.workUnits}`;
    progressText.style.cssText = [
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center",
      "font-size:9px;color:#fff;text-shadow:0 1px 1px #000",
    ].join(";");
    progressWrap.append(progressFill, progressText);

    this.projectDeadlineText = document.createElement("div");
    this.projectDeadlineText.style.cssText = "font-size:11px;font-weight:700;margin-bottom:10px";

    const rewardLine = document.createElement("div");
    rewardLine.textContent = view.assigned
      ? "🏢 회사 배정 업무 — 보수 없음(월급에 포함), 완료 시 고과 +1"
      : view.sideJob
        ? `💼 부업 — 보수 ${view.rewardGold}G (원천징수 전) · 작업마다 발각 위험`
        : `보수 ${view.rewardGold}G (원천징수 전)`;
    rewardLine.style.cssText = "font-size:11px;color:rgba(255,255,255,0.6);margin-bottom:10px";

    const blocked = !!view.pendingEvent;
    const workBtn = document.createElement("button");
    workBtn.textContent = blocked ? "이벤트 응답 대기 중..." : "🛠 작업하기";
    workBtn.disabled = blocked;
    workBtn.style.cssText = [
      "width:100%;padding:8px;border:none;border-radius:6px;font:700 13px monospace",
      blocked
        ? "background:rgba(255,255,255,0.15);color:rgba(255,255,255,0.4);cursor:not-allowed"
        : "background:#3d8b4e;color:#fff;cursor:pointer",
    ].join(";");
    if (!blocked) workBtn.addEventListener("click", () => this.doProjectWork(slot));

    this.projectPanelBody.append(title, desc, progressWrap, this.projectDeadlineText, rewardLine, workBtn);
    this.tickProjectCountdown();
  }

  private renderProjectMarket(sideJob: boolean) {
    if (!this.projectMarket.length) {
      const empty = document.createElement("div");
      empty.textContent = "수주 가능한 의뢰가 없습니다.";
      empty.style.cssText = "font-size:12px;color:rgba(255,255,255,0.5)";
      this.projectPanelBody.appendChild(empty);
      return;
    }

    for (const t of this.projectMarket) {
      const card = document.createElement("div");
      card.style.cssText = [
        "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12)",
        "border-radius:8px;padding:10px;margin-bottom:8px",
      ].join(";");

      const title = document.createElement("div");
      title.textContent = `${t.title}  ${"⭐".repeat(t.difficulty)}`;
      title.style.cssText = "font-size:12px;font-weight:700;margin-bottom:4px";

      const desc = document.createElement("div");
      desc.textContent = t.description;
      desc.style.cssText = "font-size:10px;line-height:1.5;color:rgba(255,255,255,0.7);margin-bottom:6px";

      const stats = document.createElement("div");
      stats.textContent =
        `보수 ${t.rewardGold}G · 마감 ${t.deadlineHours}시간 · 작업 ${t.workUnits}회 · 요구 Lv${t.requiredLevel}`;
      stats.style.cssText = "font-size:10px;color:#f6c945;margin-bottom:8px";

      const btn = document.createElement("button");
      btn.textContent = sideJob ? "몰래 수주하기" : "수주하기";
      btn.style.cssText = [
        "width:100%;padding:6px;border:none;border-radius:6px",
        "background:#3d6bb0;color:#fff;font:700 12px monospace;cursor:pointer",
      ].join(";");
      btn.addEventListener("click", () =>
        sideJob ? this.confirmSideJob(t.id) : void this.acceptProjectTemplate(t.id));

      card.append(title, desc, stats, btn);
      this.projectPanelBody.appendChild(card);
    }
  }

  // 정규직 + 진행 중인 배정 업무가 없을 때 (다음 3일 주기 배정을 기다리는 중)
  private renderCompanyIdle() {
    const company = this.employment?.company;
    const msg = document.createElement("div");
    msg.textContent = company
      ? `"${company.name}" 소속입니다. 다음 업무가 배정될 때까지 기다려주세요.`
      : "소속 정보를 불러오는 중...";
    msg.style.cssText = "font-size:12px;color:rgba(255,255,255,0.7);margin-bottom:12px;line-height:1.5";
    this.projectPanelBody.appendChild(msg);

    const resignBtn = document.createElement("button");
    resignBtn.textContent = "퇴사하기";
    resignBtn.style.cssText = [
      "width:100%;padding:8px;border:none;border-radius:6px",
      "background:#a83232;color:#fff;font:700 13px monospace;cursor:pointer",
    ].join(";");
    resignBtn.addEventListener("click", () => this.confirmResign());
    this.projectPanelBody.appendChild(resignBtn);
  }

  // 회사 목록 — 프리랜서에겐 "재취업", 정규직에겐 "채용 공고"(이직 동선)
  private renderCompanyList() {
    const companies = this.employment?.companies ?? [];
    if (!companies.length) return;
    const employed = this.employment?.employed ?? false;

    const divider = document.createElement("div");
    divider.textContent = employed ? "📢 채용 공고" : "🏢 재취업";
    divider.style.cssText = [
      "border-top:1px solid rgba(255,255,255,0.15);margin-top:12px;padding-top:10px;",
      "font-size:13px;font-weight:700;margin-bottom:8px",
    ].join("");
    this.projectPanelBody.appendChild(divider);

    for (const c of companies) {
      const card = document.createElement("div");
      card.style.cssText = [
        "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12)",
        "border-radius:8px;padding:10px;margin-bottom:8px",
      ].join(";");

      const title = document.createElement("div");
      title.textContent = c.current ? `${c.name} 📍` : c.name;
      title.style.cssText = "font-size:12px;font-weight:700;margin-bottom:4px";

      const desc = document.createElement("div");
      desc.textContent = c.description;
      desc.style.cssText = "font-size:10px;line-height:1.5;color:rgba(255,255,255,0.7);margin-bottom:6px";

      const stats = document.createElement("div");
      const rankReq = c.minRank ? ` · ${c.minRank} 이상` : "";
      stats.textContent = `주급 ${c.salary}G · 요구 Lv${c.minLevel}${rankReq}`;
      stats.style.cssText = "font-size:10px;color:#f6c945;margin-bottom:8px";

      card.append(title, desc, stats);

      if (c.current) {
        const badge = document.createElement("div");
        badge.textContent = "📍 현재 소속";
        badge.style.cssText = "font-size:11px;font-weight:700;color:#2ecc71;text-align:center;padding:4px 0";
        card.appendChild(badge);
      } else {
        const btn = document.createElement("button");
        btn.textContent = c.eligible
          ? (employed ? "이직 지원" : "지원하기")
          : `🔒 ${c.lockReasons.join(" · ")}`;
        btn.disabled = !c.eligible;
        btn.style.cssText = [
          "width:100%;padding:6px;border:none;border-radius:6px;font:700 12px monospace",
          c.eligible
            ? "background:#3d6bb0;color:#fff;cursor:pointer"
            : "background:rgba(255,255,255,0.15);color:rgba(255,255,255,0.4);cursor:not-allowed",
        ].join(";");
        if (c.eligible) {
          btn.addEventListener("click", () => {
            if (employed) this.confirmChangeJob(c);
            else void this.applyForCompany(c.id);
          });
        }
        card.appendChild(btn);
      }

      this.projectPanelBody.appendChild(card);
    }
  }

  private confirmChangeJob(c: CompanyView) {
    this.showConfirm(
      `현 회사에 사직서를 던집니다.\n"${c.name}"(주급 ${c.salary}G)으로 이직하시겠습니까?`,
      () => void this.applyForCompany(c.id)
    );
  }

  private formatCountdown(ms: number): string {
    if (ms <= 0) return "마감 초과";
    const totalSec = Math.floor(ms / 1000);
    const days  = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const mins  = Math.floor((totalSec % 3600) / 60);
    const secs  = totalSec % 60;
    const hhmmss = [hours, mins, secs].map((n) => String(n).padStart(2, "0")).join(":");
    return days > 0 ? `${days}일 ${hhmmss}` : hhmmss;
  }

  private tickProjectCountdown() {
    if (!this.projectPanelOpen || !this.displayedProject || !this.projectDeadlineText) return;
    const remainMs = new Date(this.displayedProject.deadlineAt).getTime() - Date.now();
    this.projectDeadlineText.textContent = `⏰ 마감까지 ${this.formatCountdown(remainMs)}`;
    this.projectDeadlineText.style.color = remainMs < 24 * 60 * 60 * 1000 ? "#e74c3c" : "#f6c945";
  }

  private buildProjectEventModal() {
    this.projectEventModal = document.createElement("div");
    this.projectEventModal.style.cssText = [
      "position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:155",
      "width:min(420px,90vw);background:rgba(10,10,20,0.95);border:1px solid #e74c3c",
      "border-radius:10px;padding:18px 20px;font-family:monospace;color:#fff;display:none",
    ].join(";");

    const label = document.createElement("div");
    label.textContent = "⚠️ 돌발 상황";
    label.style.cssText = "color:#e74c3c;font-weight:700;font-size:13px;margin-bottom:10px";

    this.projectEventSituation = document.createElement("div");
    this.projectEventSituation.style.cssText = "font-size:14px;line-height:1.6;margin-bottom:14px";

    this.projectEventChoices = document.createElement("div");
    this.projectEventChoices.style.cssText = "display:flex;flex-direction:column;gap:8px";

    this.projectEventModal.append(label, this.projectEventSituation, this.projectEventChoices);
    document.body.appendChild(this.trackDom(this.projectEventModal));
  }

  private updateProjectEventModal() {
    // 본업/부업 어느 쪽이든 대기 이벤트가 있으면 표시 — 본업 우선, 하나씩 해소.
    const mainEvent = this.projectMain?.pendingEvent ?? null;
    const eventSlot: "main" | "side" = mainEvent ? "main" : "side";
    const event = mainEvent ?? this.projectSide?.pendingEvent ?? null;
    if (!event) {
      this.projectEventModal.style.display = "none";
      return;
    }

    this.projectEventSituation.textContent = event.situation;
    this.projectEventChoices.innerHTML = "";
    for (const choice of event.choices) {
      const btn = document.createElement("button");
      btn.textContent = `[${choice.id}] ${choice.label}`;
      btn.style.cssText = [
        "padding:9px 12px;border:1px solid rgba(255,255,255,0.25);border-radius:6px",
        "background:rgba(255,255,255,0.08);color:#fff;font:600 12px monospace",
        "cursor:pointer;text-align:left",
      ].join(";");
      btn.onmouseenter = () => { btn.style.background = "rgba(255,255,255,0.18)"; };
      btn.onmouseleave = () => { btn.style.background = "rgba(255,255,255,0.08)"; };
      btn.addEventListener("click", () => {
        this.doProjectChoice(eventSlot, choice.id);
        // Prevent double-submit until the next project_state arrives
        Array.from(this.projectEventChoices.children).forEach((el) => {
          (el as HTMLButtonElement).disabled = true;
          (el as HTMLElement).style.opacity = "0.5";
        });
      });
      this.projectEventChoices.appendChild(btn);
    }
    this.projectEventModal.style.display = "block";
  }

  // ─── Confirm modal (replaces window.confirm) ────────────────────────────

  private buildConfirmModal() {
    this.confirmModal = document.createElement("div");
    this.confirmModal.style.cssText = [
      "position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:160",
      "width:min(360px,90vw);background:rgba(10,10,20,0.95);border:1px solid #3d6bb0",
      "border-radius:10px;padding:18px 20px;font-family:monospace;color:#fff;display:none",
    ].join(";");

    this.confirmMessage = document.createElement("div");
    this.confirmMessage.style.cssText =
      "font-size:14px;line-height:1.6;margin-bottom:16px;white-space:pre-line";

    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:8px";

    this.confirmNoBtn = document.createElement("button");
    this.confirmNoBtn.textContent = "취소";
    this.confirmNoBtn.style.cssText = [
      "flex:1;padding:9px 12px;border:1px solid rgba(255,255,255,0.25);border-radius:6px",
      "background:rgba(255,255,255,0.08);color:#fff;font:600 12px monospace;cursor:pointer",
    ].join(";");

    this.confirmYesBtn = document.createElement("button");
    this.confirmYesBtn.textContent = "확인";
    this.confirmYesBtn.style.cssText = [
      "flex:1;padding:9px 12px;border:none;border-radius:6px",
      "background:#3d6bb0;color:#fff;font:700 12px monospace;cursor:pointer",
    ].join(";");

    btnRow.append(this.confirmNoBtn, this.confirmYesBtn);
    this.confirmModal.append(this.confirmMessage, btnRow);
    document.body.appendChild(this.trackDom(this.confirmModal));

    this.confirmNoBtn.addEventListener("click", () => this.hideConfirm());
  }

  private hideConfirm() {
    this.confirmModal.style.display = "none";
    this.confirmYesBtn.onclick = null;
  }

  private showConfirm(message: string, onConfirm: () => void) {
    this.confirmMessage.textContent = message;
    this.confirmYesBtn.onclick = () => {
      this.hideConfirm();
      onConfirm();
    };
    this.confirmModal.style.display = "block";
  }

  // ─── 전직 시험 (인사팀 차장 NPC) ─────────────────────────────────────────

  private buildExamModal() {
    this.examModal = document.createElement("div");
    this.examModal.style.cssText = [
      "position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:158",
      "width:min(500px,92vw);max-height:min(80vh,640px);overflow-y:auto",
      "background:rgba(10,10,25,0.97);border:1px solid #2e86c1;border-radius:10px",
      "padding:18px 20px;box-sizing:border-box;font-family:monospace;color:#fff;display:none",
    ].join(";");

    const header = document.createElement("div");
    header.textContent = "📝 전직 시험 — 수습 → 주니어";
    header.style.cssText = [
      "font-size:15px;font-weight:700;color:#5dade2;margin-bottom:12px",
      "border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:8px",
    ].join(";");

    this.examBody = document.createElement("div");

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "닫기";
    closeBtn.dataset.exam = "close";
    closeBtn.style.cssText = [
      "width:100%;margin-top:14px;padding:8px;border:1px solid rgba(255,255,255,0.25)",
      "border-radius:6px;background:rgba(255,255,255,0.08);color:#fff",
      "font:600 12px monospace;cursor:pointer",
    ].join(";");
    closeBtn.addEventListener("click", () => this.closeExamModal());

    this.examModal.append(header, this.examBody, closeBtn);
    document.body.appendChild(this.trackDom(this.examModal));

    // 재응시 쿨다운 남은 시간 실시간 갱신
    this.time.addEvent({ delay: 1000, loop: true, callback: () => this.tickExamCooldown() });
  }

  private closeExamModal() {
    this.examOpen = false;
    this.examModal.style.display = "none";
  }

  private examMessage(text: string) {
    this.examBody.innerHTML = "";
    const msg = document.createElement("div");
    msg.textContent = text;
    msg.style.cssText = "font-size:13px;line-height:1.6;color:rgba(255,255,255,0.8)";
    this.examBody.appendChild(msg);
  }

  private async openExamModal() {
    this.examOpen = true;
    this.examModal.style.display = "block";
    this.examMessage("불러오는 중...");
    try {
      const res = await apiFetch(`${API_URL}/exam/status`, { credentials: "include" });
      if (!res.ok) { this.examMessage("시험 정보를 불러오지 못했습니다."); return; }
      this.renderExamStatus(await res.json() as ExamStatusView);
    } catch {
      this.examMessage("서버에 연결할 수 없습니다.");
    }
  }

  private renderExamStatus(status: ExamStatusView) {
    this.examBody.innerHTML = "";
    this.examCooldownEl = null;

    if (status.alreadyPromoted) {
      this.examMessage(`이미 "${status.rank}"입니다. 다음 승진 시험은 인사팀에서 준비 중이라고 합니다. (다음 버전)`);
      return;
    }

    // 시작해놓고 제출하지 않은 시험이 있으면 바로 이어서 푼다
    if (status.inProgress) {
      this.renderExamQuestions(status.inProgress);
      return;
    }

    const intro = document.createElement("div");
    intro.textContent =
      `${status.skillsRequired}종의 수습 스킬을 전부 완료하고 집중력 ${status.staminaCost}을 소모해 응시합니다. ` +
      `5문제 중 4문제 이상 맞히면 합격!`;
    intro.style.cssText = "font-size:12px;line-height:1.6;color:rgba(255,255,255,0.75);margin-bottom:12px";
    this.examBody.appendChild(intro);

    const skillLine = document.createElement("div");
    const skillsOk = status.skillsCompleted >= status.skillsRequired;
    skillLine.textContent =
      `${skillsOk ? "✅" : "❌"} 수습 스킬 ${status.skillsCompleted}/${status.skillsRequired} 완료`;
    skillLine.style.cssText = `font-size:12px;margin-bottom:6px;color:${skillsOk ? "#2ecc71" : "#e74c3c"}`;
    this.examBody.appendChild(skillLine);

    if (status.cooldownRemainingMs > 0) {
      // 불합격 쿨다운 — 재응시 버튼에 남은 시간 표시 (1초마다 갱신)
      this.examCooldownUntilMs = Date.now() + status.cooldownRemainingMs;
      const btn = document.createElement("button");
      btn.disabled = true;
      btn.dataset.exam = "cooldown";
      btn.style.cssText = [
        "width:100%;margin-top:10px;padding:10px;border:none;border-radius:6px",
        "background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.5)",
        "font:700 13px monospace;cursor:not-allowed",
      ].join(";");
      this.examBody.appendChild(btn);
      this.examCooldownEl = btn as unknown as HTMLDivElement;
      this.tickExamCooldown();
      return;
    }

    for (const reason of status.reasons) {
      const line = document.createElement("div");
      line.textContent = `❌ ${reason}`;
      line.style.cssText = "font-size:12px;margin-bottom:6px;color:#e74c3c";
      this.examBody.appendChild(line);
    }

    const startBtn = document.createElement("button");
    startBtn.textContent = status.eligible
      ? `응시하기 (집중력 ${status.staminaCost} 소모)`
      : "응시 조건 미달";
    startBtn.disabled = !status.eligible;
    startBtn.dataset.exam = "start";
    startBtn.style.cssText = [
      "width:100%;margin-top:10px;padding:10px;border:none;border-radius:6px;font:700 13px monospace",
      status.eligible
        ? "background:#2e86c1;color:#fff;cursor:pointer"
        : "background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.5);cursor:not-allowed",
    ].join(";");
    if (status.eligible) startBtn.addEventListener("click", () => void this.startExam());
    this.examBody.appendChild(startBtn);
  }

  private tickExamCooldown() {
    if (!this.examOpen || !this.examCooldownEl) return;
    const remain = this.examCooldownUntilMs - Date.now();
    if (remain <= 0) {
      // 쿨다운이 방금 끝났다 — 상태를 다시 불러오면 응시 버튼이 열린다
      this.examCooldownEl = null;
      void this.openExamModal();
      return;
    }
    this.examCooldownEl.textContent = `재응시까지 ${this.formatCountdown(remain)}`;
  }

  private async startExam() {
    this.examMessage("출제 중...");
    try {
      const res = await apiFetch(`${API_URL}/exam/start`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json() as { questions?: ExamQuestionView[]; error?: string };
      if (!res.ok || !data.questions) {
        this.showToast(data.error ?? "응시할 수 없습니다", "#e74c3c", 2000);
        void this.openExamModal();
        return;
      }
      this.renderExamQuestions(data.questions);
    } catch {
      this.examMessage("서버에 연결할 수 없습니다.");
    }
  }

  private renderExamQuestions(questions: ExamQuestionView[]) {
    this.examBody.innerHTML = "";
    this.examSelections.clear();

    const choiceButtons = new Map<number, HTMLButtonElement[]>();

    const submitBtn = document.createElement("button");
    submitBtn.textContent = `제출하기 (0/${questions.length} 응답)`;
    submitBtn.disabled = true;
    submitBtn.dataset.exam = "submit";
    submitBtn.style.cssText = [
      "width:100%;margin-top:6px;padding:10px;border:none;border-radius:6px",
      "background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.5)",
      "font:700 13px monospace;cursor:not-allowed",
    ].join(";");

    const refreshSubmit = () => {
      const answered = this.examSelections.size;
      submitBtn.textContent = `제출하기 (${answered}/${questions.length} 응답)`;
      const ready = answered === questions.length;
      submitBtn.disabled = !ready;
      submitBtn.style.background = ready ? "#2e86c1" : "rgba(255,255,255,0.12)";
      submitBtn.style.color = ready ? "#fff" : "rgba(255,255,255,0.5)";
      submitBtn.style.cursor = ready ? "pointer" : "not-allowed";
    };

    questions.forEach((q, qi) => {
      const card = document.createElement("div");
      card.style.cssText = [
        "background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.12)",
        "border-radius:8px;padding:10px 12px;margin-bottom:10px",
      ].join(";");

      const text = document.createElement("div");
      text.textContent = `Q${qi + 1}. ${q.text}`;
      text.style.cssText = "font-size:12px;font-weight:700;line-height:1.5;margin-bottom:8px";
      card.appendChild(text);

      const btns: HTMLButtonElement[] = [];
      q.choices.forEach((choice, ci) => {
        const btn = document.createElement("button");
        btn.textContent = `${ci + 1}) ${choice}`;
        btn.dataset.examQ = String(q.id);
        btn.dataset.examC = String(ci);
        btn.style.cssText = [
          "display:block;width:100%;text-align:left;margin-bottom:4px;padding:7px 10px",
          "border:1px solid rgba(255,255,255,0.18);border-radius:6px",
          "background:rgba(255,255,255,0.06);color:rgba(255,255,255,0.85)",
          "font:500 11px monospace;cursor:pointer;line-height:1.4",
        ].join(";");
        btn.addEventListener("click", () => {
          this.examSelections.set(q.id, ci);
          for (const b of btns) {
            b.style.background = "rgba(255,255,255,0.06)";
            b.style.borderColor = "rgba(255,255,255,0.18)";
          }
          btn.style.background = "rgba(46,134,193,0.35)";
          btn.style.borderColor = "#2e86c1";
          refreshSubmit();
        });
        btns.push(btn);
        card.appendChild(btn);
      });
      choiceButtons.set(q.id, btns);

      this.examBody.appendChild(card);
    });

    submitBtn.addEventListener("click", () => void this.submitExamAnswers());
    this.examBody.appendChild(submitBtn);
  }

  private async submitExamAnswers() {
    const answers: Record<string, number> = {};
    for (const [qid, ci] of this.examSelections) answers[String(qid)] = ci;

    this.examMessage("채점 중...");
    try {
      const res = await apiFetch(`${API_URL}/exam/submit`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
      });
      const data = await res.json() as ExamResultView & { error?: string };
      if (!res.ok) {
        this.showToast(data.error ?? "제출할 수 없습니다", "#e74c3c", 2000);
        void this.openExamModal();
        return;
      }
      this.renderExamResult(data);
    } catch {
      this.examMessage("서버에 연결할 수 없습니다.");
    }
  }

  private renderExamResult(result: ExamResultView) {
    this.examBody.innerHTML = "";
    this.examCooldownEl = null;

    const headline = document.createElement("div");
    headline.textContent = result.passed ? "🎉 합격!" : "😢 불합격";
    headline.dataset.exam = "result";
    headline.style.cssText =
      `font-size:20px;font-weight:700;text-align:center;margin-bottom:10px;color:${result.passed ? "#f6c945" : "#e74c3c"}`;

    const score = document.createElement("div");
    score.textContent = `정답 ${result.correctCount}/${result.total} (합격 기준 ${result.passCount}문제)`;
    score.style.cssText = "font-size:13px;text-align:center;margin-bottom:12px;color:rgba(255,255,255,0.8)";

    this.examBody.append(headline, score);

    if (result.passed) {
      const detail = document.createElement("div");
      detail.textContent =
        `이제 "${result.newRank}"입니다. 집중력 최대치 +${result.staminaMaxBonus}! 인사팀 차장이 무뚝뚝하게 악수를 청한다.`;
      detail.style.cssText = "font-size:12px;line-height:1.6;text-align:center;color:rgba(255,255,255,0.85)";
      this.examBody.appendChild(detail);
      // 축하 연출은 서버 "promoted" 푸시(토스트+채팅 로그)와 함께 표시된다
    } else {
      // 불합격 — 72시간 쿨다운 버튼에 남은 시간 표시
      this.examCooldownUntilMs = result.cooldownUntil
        ? new Date(result.cooldownUntil).getTime()
        : Date.now();
      const btn = document.createElement("button");
      btn.disabled = true;
      btn.dataset.exam = "cooldown";
      btn.style.cssText = [
        "width:100%;margin-top:10px;padding:10px;border:none;border-radius:6px",
        "background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.5)",
        "font:700 13px monospace;cursor:not-allowed",
      ].join(";");
      this.examBody.appendChild(btn);
      this.examCooldownEl = btn as unknown as HTMLDivElement;
      this.tickExamCooldown();
    }
  }

  // ─── Skill tree ────────────────────────────────────────────────────────

  private buildSkillPanel() {
    this.buildSideButton("📚 스킬", 100, () => this.toggleSkillPanel());

    this.skillPanel = document.createElement("div");
    this.skillPanel.style.cssText = this.sidePanelCss();

    const header = document.createElement("div");
    header.textContent = "📚 스킬 학습";
    header.style.cssText = [
      "font-size:14px;font-weight:700;margin-bottom:10px",
      "border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:8px",
    ].join(";");

    this.skillPanelBody = document.createElement("div");
    this.skillPanel.append(header, this.skillPanelBody);
    document.body.appendChild(this.trackDom(this.skillPanel));

    this.time.addEvent({ delay: 1000, loop: true, callback: () => this.tickSkillCountdowns() });
  }

  private toggleSkillPanel() {
    this.togglePanel("skill");
  }

  private async loadSkills() {
    try {
      const res = await apiFetch(`${API_URL}/skills`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json() as { skills: SkillView[]; anyLearning: boolean };
      this.skillList = data.skills;
      this.skillAnyLearning = data.anyLearning;
      this.renderSkillPanel();
    } catch { /* ignore */ }
  }

  private renderSkillPanel() {
    this.skillPanelBody.innerHTML = "";
    this.skillCountdownEls.clear();

    for (const s of this.skillList) {
      const card = document.createElement("div");
      card.style.cssText = [
        "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12)",
        "border-radius:8px;padding:10px;margin-bottom:8px",
      ].join(";");

      const titleRow = document.createElement("div");
      titleRow.style.cssText = "display:flex;justify-content:space-between;align-items:center;margin-bottom:4px";
      const title = document.createElement("div");
      title.textContent = `${skillStatusIcon(s.status)} ${s.name}`;
      title.style.cssText = "font-size:12px;font-weight:700";
      const bonus = document.createElement("div");
      bonus.textContent = skillBonusLabel(s.bonusType, s.bonusValue);
      bonus.style.cssText = "font-size:10px;color:#f6c945";
      titleRow.append(title, bonus);

      const desc = document.createElement("div");
      desc.textContent = s.description;
      desc.style.cssText = "font-size:10px;line-height:1.5;color:rgba(255,255,255,0.65);margin-bottom:6px";

      const meta = document.createElement("div");
      meta.textContent =
        `학습 시간 ${s.durationMinutes}분 · 요구 Lv${s.requiredLevel} · 교재비 ${s.textbookCost}G`;
      meta.style.cssText = "font-size:10px;color:rgba(255,255,255,0.5);margin-bottom:6px";

      card.append(titleRow, desc, meta);

      if (s.status === "learning") {
        const countdown = document.createElement("div");
        countdown.style.cssText = "font-size:11px;font-weight:700;color:#6c9bf0";
        this.skillCountdownEls.set(s.id, countdown);
        this.updateSkillCountdownEl(countdown, s.remainingMs ?? 0);
        card.appendChild(countdown);
      } else if (s.status === "locked") {
        const reason = document.createElement("div");
        reason.textContent = `🔒 ${s.lockedReason}`;
        reason.style.cssText = "font-size:11px;color:rgba(255,255,255,0.45)";
        card.appendChild(reason);
      } else if (s.status === "available") {
        const disabled = this.skillAnyLearning;
        const btn = document.createElement("button");
        btn.textContent = disabled ? "다른 스킬 학습 중" : "학습 시작";
        btn.disabled = disabled;
        btn.style.cssText = [
          "width:100%;padding:6px;border:none;border-radius:6px;font:700 12px monospace",
          disabled
            ? "background:rgba(255,255,255,0.15);color:rgba(255,255,255,0.4);cursor:not-allowed"
            : "background:#3d6bb0;color:#fff;cursor:pointer",
        ].join(";");
        if (!disabled) btn.addEventListener("click", () => this.confirmLearnSkill(s));
        card.appendChild(btn);
      }

      this.skillPanelBody.appendChild(card);
    }
  }

  private updateSkillCountdownEl(el: HTMLDivElement, remainingMs: number) {
    el.textContent = remainingMs > 0 ? `⏳ 완료까지 ${this.formatCountdown(remainingMs)}` : "완료 처리 중...";
  }

  private tickSkillCountdowns() {
    if (!this.skillPanelOpen || !this.skillCountdownEls.size) return;
    for (const [skillId, el] of this.skillCountdownEls) {
      const skill = this.skillList.find((s) => s.id === skillId);
      if (!skill || skill.remainingMs === undefined) continue;
      skill.remainingMs = Math.max(0, skill.remainingMs - 1000);
      this.updateSkillCountdownEl(el, skill.remainingMs);
    }
  }

  private confirmLearnSkill(s: SkillView) {
    this.showConfirm(
      `"${s.name}"을(를) 학습하시겠습니까?\n소요 시간: ${s.durationMinutes}분\n집중력 ${SKILL_LEARN_STAMINA_COST} 소모\n📖 교재 "${s.textbookName}" ${s.textbookCost}G`,
      () => void this.startLearningSkill(s.id)
    );
  }

  private async startLearningSkill(skillId: number) {
    try {
      const res = await apiFetch(`${API_URL}/skills/learn`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ skillId }),
      });
      const data = await res.json() as { skill?: SkillView; gold?: number; error?: string };
      if (!res.ok || !data.skill) {
        this.showToast(data.error ?? "학습을 시작할 수 없습니다", "#e74c3c", 1800);
        return;
      }
      this.showToast(`📚 "${data.skill.name}" 학습 시작!`, "#6c9bf0", 1800);
      this.pushBattleLog([
        `📚 "${data.skill.name}" 학습을 시작했습니다. (${data.skill.durationMinutes}분 소요)`,
        `-${data.skill.textbookCost}G (교재: ${data.skill.textbookName})`,
      ]);
      if (typeof data.gold === "number") this.updateGoldHud(data.gold);
      void this.loadSkills();
    } catch {
      this.showToast("서버 연결 실패", "#e74c3c", 1800);
    }
  }

  // ─── 몬스터 도감 ─────────────────────────────────────────────────────────

  private buildCodexPanel() {
    this.buildSideButton("📖 도감", 188, () => this.toggleCodexPanel());

    this.codexPanel = document.createElement("div");
    this.codexPanel.style.cssText = this.sidePanelCss();

    this.codexPanelHeader = document.createElement("div");
    this.codexPanelHeader.textContent = "📖 몬스터 도감";
    this.codexPanelHeader.style.cssText = [
      "font-size:14px;font-weight:700;margin-bottom:10px",
      "border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:8px",
    ].join(";");

    this.codexPanelBody = document.createElement("div");
    this.codexPanel.append(this.codexPanelHeader, this.codexPanelBody);
    document.body.appendChild(this.trackDom(this.codexPanel));
  }

  private toggleCodexPanel() {
    this.togglePanel("codex");
  }

  private async loadCodex() {
    try {
      const res = await apiFetch(`${API_URL}/codex`, { credentials: "include" });
      if (!res.ok) return;
      this.renderCodexPanel(await res.json() as CodexView);
    } catch { /* ignore */ }
  }

  private renderCodexPanel(view: CodexView) {
    this.codexPanelHeader.textContent =
      `📖 몬스터 도감 (${view.discoveredCount}/${view.totalCount})` + (view.completed ? " 🏆" : "");
    this.codexPanelBody.innerHTML = "";

    if (view.completed) {
      const done = document.createElement("div");
      done.textContent = `🏆 칭호 "버그 헌터" — 모든 몬스터를 도감에 등록했다!`;
      done.style.cssText = "font-size:11px;color:#f6c945;margin-bottom:10px;line-height:1.5";
      this.codexPanelBody.appendChild(done);
    }

    for (const m of MONSTERS) {
      const entry = view.entries[m.id];
      const card = document.createElement("div");
      card.style.cssText = [
        "display:flex;gap:10px;align-items:flex-start",
        "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12)",
        "border-radius:8px;padding:10px;margin-bottom:8px",
      ].join(";");

      // 발견: 본색 마커 / 미발견: 어두운 실루엣
      const marker = document.createElement("div");
      const color = entry ? `#${m.color.toString(16).padStart(6, "0")}` : "#222";
      marker.style.cssText = [
        `width:${m.radius * 2 + 6}px;height:${m.radius * 2 + 6}px;flex-shrink:0;margin-top:2px`,
        `background:${color};border:1px solid rgba(255,255,255,${entry ? "0.4" : "0.15"})`,
        m.shape === "circle" ? "border-radius:50%" : "border-radius:3px",
      ].join(";");

      const info = document.createElement("div");
      info.style.cssText = "flex:1;min-width:0";

      const name = document.createElement("div");
      name.textContent = entry ? m.label : "???";
      name.style.cssText =
        `font-size:12px;font-weight:700;margin-bottom:3px;color:${entry ? "#fff" : "rgba(255,255,255,0.4)"}`;

      const desc = document.createElement("div");
      desc.textContent = entry ? m.codex : "아직 만나지 못한 몬스터다.";
      desc.style.cssText =
        `font-size:10px;line-height:1.5;color:rgba(255,255,255,${entry ? "0.7" : "0.35"})`;

      info.append(name, desc);
      if (entry) {
        const kills = document.createElement("div");
        kills.textContent = `처치 ${entry.killCount}회`;
        kills.style.cssText = "font-size:10px;color:#f6c945;margin-top:4px";
        info.appendChild(kills);
      }

      card.append(marker, info);
      this.codexPanelBody.appendChild(card);
    }
  }

  // ─── Travel ──────────────────────────────────────────────────────────────

  private buildTravelPanel() {
    this.buildSideButton("🚇 이동", 144, () => this.toggleTravelPanel());

    this.travelPanel = document.createElement("div");
    this.travelPanel.style.cssText = this.sidePanelCss();

    const header = document.createElement("div");
    header.textContent = "🚇 존 이동";
    header.style.cssText = [
      "font-size:14px;font-weight:700;margin-bottom:10px",
      "border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:8px",
    ].join(";");

    this.travelPanelBody = document.createElement("div");
    this.travelPanel.append(header, this.travelPanelBody);
    document.body.appendChild(this.trackDom(this.travelPanel));
  }

  private toggleTravelPanel() {
    this.togglePanel("travel");
  }

  private renderTravelPanel() {
    this.travelPanelBody.innerHTML = "";
    for (const zoneId of ZONE_IDS) {
      const card = document.createElement("div");
      card.style.cssText = [
        "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12)",
        "border-radius:8px;padding:10px;margin-bottom:8px",
      ].join(";");

      const title = document.createElement("div");
      title.textContent = ZONES[zoneId].name;
      title.style.cssText = "font-size:13px;font-weight:700;margin-bottom:6px";
      card.appendChild(title);

      if (zoneId === this.zone) {
        const here = document.createElement("div");
        here.textContent = "📍 현재 위치";
        here.style.cssText = "font-size:11px;color:#2ecc71";
        card.appendChild(here);
      } else {
        const btnRow = document.createElement("div");
        btnRow.style.cssText = "display:flex;gap:8px";
        for (const method of Object.values(TRAVEL_METHODS)) {
          const cost = method.staminaCost > 0 ? `집중력 ${method.staminaCost}` : `${method.goldCost}G`;
          const btn = document.createElement("button");
          btn.textContent = `${method.label} (${cost})`;
          btn.style.cssText = [
            "flex:1;padding:7px 8px;border:none;border-radius:6px",
            "background:#3d6bb0;color:#fff;font:700 11px monospace;cursor:pointer",
          ].join(";");
          btn.addEventListener("click", () => this.doTravel(zoneId, method.id));
          btnRow.appendChild(btn);
        }
        card.appendChild(btnRow);
      }

      this.travelPanelBody.appendChild(card);
    }
  }

  private doTravel(toZone: ZoneId, method: TravelMethod) {
    this.room.send("travel", { toZone, method });
  }

  // Server already validated + updated current_zone; now do the actual
  // Room switch — leave this zone's room, mint a fresh joinToken (the one
  // used to join this room was already single-use-consumed), then restart
  // the scene fully so the new zone's map/NPCs/monsters/HUD load clean.
  private async completeTravel(toZone: ZoneId) {
    try {
      this.room.leave();
    } catch { /* already disconnected */ }

    try {
      const res = await apiFetch(`${API_URL}/auth/me`, { credentials: "include" });
      const data = await res.json() as { joinToken?: string };
      if (!res.ok || !data.joinToken) {
        this.showToast("이동 중 오류가 발생했습니다. 새로고침 해주세요.", "#e74c3c", 2500);
        return;
      }
      this.scene.restart({
        nickname: this.nickname, job: this.job, joinToken: data.joinToken, zone: toZone,
      });
    } catch {
      this.showToast("이동 중 서버 연결에 실패했습니다.", "#e74c3c", 2500);
    }
  }

  // ─── Dashboard ("오늘의 내 상황") ──────────────────────────────────────────

  private buildDashboardOverlay() {
    this.dashboardOverlay = document.createElement("div");
    this.dashboardOverlay.style.cssText = [
      "position:fixed;inset:0;z-index:200;display:none",
      "background:rgba(10,10,30,0.97);overflow-y:auto",
      "font-family:monospace;padding:48px 20px;box-sizing:border-box",
    ].join(";");
    document.body.appendChild(this.trackDom(this.dashboardOverlay));
  }

  private buildDashboardHomeButton() {
    const btn = document.createElement("button");
    btn.textContent = "🏠";
    btn.title = "대시보드 (Tab)";
    btn.style.cssText = [
      "position:fixed;top:12px;left:220px;z-index:118",
      "background:rgba(60,60,110,0.85);border:1px solid rgba(255,255,255,0.25)",
      "color:#fff;padding:7px 12px;cursor:pointer;border-radius:6px",
      "font:600 14px monospace;transition:opacity 0.15s",
    ].join(";");
    btn.onmouseenter = () => { btn.style.opacity = "0.8"; };
    btn.onmouseleave = () => { btn.style.opacity = "1"; };
    btn.addEventListener("click", () => this.toggleDashboard());
    document.body.appendChild(this.trackDom(btn));
  }

  private toggleDashboard() {
    if (this.dashboardOpen) this.closeDashboard();
    else void this.openDashboard();
  }

  private async openDashboard() {
    this.dashboardOpen = true;
    this.dashboardOverlay.style.display = "block";
    this.renderDashboardMessage("불러오는 중...");
    try {
      const res = await apiFetch(`${API_URL}/dashboard`, { credentials: "include" });
      if (!res.ok) { this.renderDashboardMessage("대시보드를 불러오지 못했습니다."); return; }
      const data = await res.json() as DashboardData;
      this.renderDashboard(data);
    } catch {
      this.renderDashboardMessage("서버에 연결할 수 없습니다.");
    }
  }

  private closeDashboard() {
    this.dashboardOpen = false;
    this.dashboardOverlay.style.display = "none";
  }

  private renderDashboardMessage(text: string) {
    this.dashboardOverlay.innerHTML = "";
    const msg = document.createElement("div");
    msg.textContent = text;
    msg.style.cssText = "color:rgba(255,255,255,0.6);text-align:center;margin-top:120px;font-size:14px";
    this.dashboardOverlay.appendChild(msg);
  }

  private dashboardCard(icon: string, title: string): { card: HTMLDivElement; body: HTMLDivElement } {
    const card = document.createElement("div");
    card.style.cssText = [
      "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.14)",
      "border-radius:10px;padding:14px 16px;margin-bottom:12px",
    ].join(";");
    const header = document.createElement("div");
    header.textContent = `${icon} ${title}`;
    header.style.cssText = "font-size:13px;font-weight:700;color:#f6c945;margin-bottom:8px";
    const body = document.createElement("div");
    card.append(header, body);
    return { card, body };
  }

  private renderDashboard(data: DashboardData) {
    this.dashboardOverlay.innerHTML = "";

    const wrap = document.createElement("div");
    wrap.style.cssText = "width:560px;max-width:100%;margin:0 auto;color:#fff";

    const headline = document.createElement("div");
    headline.textContent = data.headline;
    headline.style.cssText = "font-size:22px;font-weight:700;text-align:center;margin-bottom:4px";

    const greeting = document.createElement("div");
    greeting.textContent = data.greetingLine;
    greeting.style.cssText = "font-size:13px;color:rgba(255,255,255,0.55);text-align:center;margin-bottom:24px";

    wrap.append(headline, greeting);

    // ⚡ 집중력
    {
      const { card, body } = this.dashboardCard("⚡", "집중력");
      const value = document.createElement("div");
      value.textContent = `${data.stamina.value} / ${data.stamina.max}`;
      value.style.cssText = "font-size:20px;font-weight:700;margin-bottom:4px";
      const sub = document.createElement("div");
      sub.textContent = data.stamina.msUntilFull > 0
        ? `완전 회복까지 ${this.formatCountdown(data.stamina.msUntilFull)}`
        : "가득 찼습니다";
      sub.style.cssText = "font-size:11px;color:rgba(255,255,255,0.6)";
      body.append(value, sub);
      wrap.appendChild(card);
    }

    // 💰 골드
    {
      const { card, body } = this.dashboardCard("💰", "골드");
      const value = document.createElement("div");
      value.textContent = `${data.gold}G`;
      value.style.cssText = "font-size:20px;font-weight:700";
      body.append(value);
      wrap.appendChild(card);
    }

    // 🏢 소속
    {
      const { card, body } = this.dashboardCard("🏢", "소속");
      if (data.rank) {
        const rankLine = document.createElement("div");
        rankLine.textContent = `직급 ${data.rank}`;
        rankLine.style.cssText = "font-size:12px;color:#5dade2;margin-bottom:4px";
        body.appendChild(rankLine);
      }
      if (data.employment.employed) {
        const name = document.createElement("div");
        name.textContent = data.employment.companyName ?? "";
        name.style.cssText = "font-size:15px;font-weight:700;margin-bottom:4px";
        const perf = document.createElement("div");
        perf.textContent = `고과 점수 ${data.employment.performanceScore}`;
        perf.style.cssText = "font-size:12px;color:rgba(255,255,255,0.7);margin-bottom:4px";
        body.append(name, perf);
        if (data.employment.nextSalaryAt) {
          const remainMs = new Date(data.employment.nextSalaryAt).getTime() - Date.now();
          const salary = document.createElement("div");
          salary.textContent = remainMs > 0
            ? `다음 월급까지 ${this.formatCountdown(remainMs)}`
            : "월급 정산 대기 중";
          salary.style.cssText = "font-size:11px;color:#f6c945";
          body.appendChild(salary);
        }
        if (data.employment.nextGoal) {
          const goal = document.createElement("div");
          goal.textContent = `🎯 다음 목표: ${data.employment.nextGoal}`;
          goal.style.cssText = "font-size:11px;color:#5dade2;margin-top:4px";
          body.appendChild(goal);
        }
      } else {
        const msg = document.createElement("div");
        msg.textContent = "🧑‍💻 프리랜서 — 소속 없음";
        msg.style.cssText = "font-size:13px;color:rgba(255,255,255,0.7)";
        body.appendChild(msg);
      }
      wrap.appendChild(card);
    }

    // 📋 프로젝트
    {
      const { card, body } = this.dashboardCard("📋", "프로젝트");
      const p = data.project.active;
      if (p) {
        const title = document.createElement("div");
        title.textContent = p.title;
        title.style.cssText = "font-size:14px;font-weight:700;margin-bottom:4px";
        const progress = document.createElement("div");
        progress.textContent = `진행도 ${p.progress}/${p.workUnits}`;
        progress.style.cssText = "font-size:12px;color:rgba(255,255,255,0.7);margin-bottom:4px";
        const remainMs = new Date(p.deadlineAt).getTime() - Date.now();
        const deadline = document.createElement("div");
        deadline.textContent = `⏰ 마감까지 ${this.formatCountdown(remainMs)}`;
        deadline.style.cssText =
          `font-size:12px;font-weight:700;color:${remainMs < 24 * 60 * 60 * 1000 ? "#e74c3c" : "#f6c945"}`;
        body.append(title, progress, deadline);
      } else {
        const msg = document.createElement("div");
        msg.textContent = "진행 중인 프로젝트가 없습니다. 마켓에서 새 의뢰를 찾아보세요.";
        msg.style.cssText = "font-size:12px;color:rgba(255,255,255,0.7);margin-bottom:4px";
        const count = document.createElement("div");
        count.textContent = `수주 가능한 의뢰 ${data.project.marketCount}건`;
        count.style.cssText = "font-size:12px;color:#f6c945";
        body.append(msg, count);
      }
      wrap.appendChild(card);
    }

    // 📋 오늘의 할 일 (튜토리얼 완료자만) — 좌측 배지도 함께 갱신
    this.dailyState = data.daily;
    this.updateDailyBadge();
    if (data.daily && data.daily.quests.length) {
      const { card, body } = this.dashboardCard("📋", "오늘의 할 일");
      for (const q of data.daily.quests) {
        const row = document.createElement("div");
        row.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:6px";

        const label = document.createElement("div");
        const icon = q.claimed ? "✅" : q.progress >= q.target ? "🎁" : "□";
        label.textContent = `${icon} ${q.label} (${q.progress}/${q.target})`;
        label.style.cssText = `flex:1;font-size:12px;color:${q.claimed ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.85)"}`;
        row.appendChild(label);

        const reward = document.createElement("div");
        reward.textContent = `+${q.rewardGold}G`;
        reward.style.cssText = "font-size:11px;color:#f6c945";
        row.appendChild(reward);

        if (!q.claimed && q.progress >= q.target) {
          const btn = document.createElement("button");
          btn.textContent = "보상 받기";
          btn.dataset.dailyClaim = String(q.id);
          btn.style.cssText = [
            "padding:4px 10px;border:none;border-radius:6px",
            "background:#3d8b4e;color:#fff;font:700 11px monospace;cursor:pointer",
          ].join(";");
          btn.addEventListener("click", () => void this.claimDaily(q.id));
          row.appendChild(btn);
        }
        body.appendChild(row);
      }
      const hint = document.createElement("div");
      hint.textContent = data.daily.allClaimed
        ? "오늘 몫은 끝났습니다. 내일 자정에 새 리포트가 도착합니다."
        : "3개 전부 완료하면 보너스 커피 ☕";
      hint.style.cssText = "font-size:11px;color:rgba(255,255,255,0.5);margin-top:4px";
      body.appendChild(hint);
      wrap.appendChild(card);
    }

    // 📚 학습 (학습 중일 때만)
    if (data.skillLearning) {
      const { card, body } = this.dashboardCard("📚", "학습");
      const name = document.createElement("div");
      name.textContent = data.skillLearning.name;
      name.style.cssText = "font-size:14px;font-weight:700;margin-bottom:4px";
      const remain = document.createElement("div");
      remain.textContent = data.skillLearning.remainingMs > 0
        ? `완료까지 ${this.formatCountdown(data.skillLearning.remainingMs)}`
        : "완료 처리 중...";
      remain.style.cssText = "font-size:12px;color:#6c9bf0";
      body.append(name, remain);
      wrap.appendChild(card);
    }

    // 💰 정산 (있을 때만)
    if (data.settlement) {
      const { card, body } = this.dashboardCard("💰", "정산");
      const total = document.createElement("div");
      total.textContent = `총 ${data.settlement.totalGold}G 입금`;
      total.style.cssText = "font-size:14px;font-weight:700;color:#2ecc71;margin-bottom:6px";
      body.appendChild(total);
      for (const item of data.settlement.items) {
        const line = document.createElement("div");
        line.textContent = `· "${item.title}" +${item.netGold}G (원천징수 -${item.taxWithheld}G)`;
        line.style.cssText = "font-size:11px;color:rgba(255,255,255,0.65)";
        body.appendChild(line);
      }
      wrap.appendChild(card);
    }

    // 🔔 알림 (있을 때만)
    if (data.notifications.length) {
      const { card, body } = this.dashboardCard("🔔", "알림");
      for (const n of data.notifications) {
        const line = document.createElement("div");
        line.textContent = n;
        line.style.cssText = "font-size:12px;color:rgba(255,255,255,0.8);margin-bottom:4px";
        body.appendChild(line);
      }
      wrap.appendChild(card);
    }

    const startBtn = document.createElement("button");
    startBtn.textContent = "게임 시작";
    startBtn.style.cssText = [
      "width:100%;padding:14px;margin-top:8px;border:none;border-radius:8px",
      "background:#3d8b4e;color:#fff;font:700 15px monospace;cursor:pointer",
    ].join(";");
    startBtn.addEventListener("click", () => this.closeDashboard());
    wrap.appendChild(startBtn);

    this.dashboardOverlay.appendChild(wrap);
  }

  private async claimDaily(questId: number) {
    try {
      const res = await apiFetch(`${API_URL}/daily/claim`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ questId }),
      });
      const data = await res.json() as { gold?: number; exp?: number; allClear?: boolean; coffee?: number; error?: string };
      if (!res.ok || typeof data.gold !== "number") {
        this.showToast(data.error ?? "보상을 받을 수 없습니다", "#e74c3c", 1800);
        return;
      }
      const bonus = data.allClear ? `\n🏆 올클리어! 보너스 커피 ${data.coffee}개` : "";
      this.showToast(`🎁 데일리 보상: +${data.gold}G, +${data.exp} EXP${bonus}`, "#2ecc71", 2500);
      await this.openDashboard(); // 카드/골드 갱신
    } catch {
      this.showToast("서버 연결 실패", "#e74c3c", 1800);
    }
  }

  // ─── Toast (level-up, zone names, quest completion) ──────────────────────

  private showToast(text: string, color = "#f6c945", durationMs = 2000) {
    const el = document.createElement("div");
    el.textContent = text;
    el.style.cssText = [
      "position:fixed;top:80px;left:50%;transform:translateX(-50%);z-index:150",
      `background:rgba(0,0,0,0.8);border:1px solid ${color};color:${color}`,
      "padding:10px 22px;border-radius:8px;font:700 16px monospace",
      "pointer-events:none;transition:opacity 0.4s;text-align:center;white-space:pre-line",
    ].join(";");
    document.body.appendChild(this.trackDom(el));
    this.time.delayedCall(durationMs, () => {
      el.style.opacity = "0";
      this.time.delayedCall(400, () => el.remove());
    });
  }

  // ─── Logout ──────────────────────────────────────────────────────────────

  private buildLogoutButton() {
    this.buildSideButton("🚪 로그아웃", 12, () => this.confirmLogout(), { danger: true });
  }

  // 다른 기기 로그인으로 밀려난 경우 (소켓 강제 종료 or API 401) — 안내와 함께
  // 로그인 화면으로 이동한다. AuthScene은 notice가 있으면 세션 자동 복원을
  // 건너뛰므로 두 기기가 서로 밀어내는 무한 루프가 생기지 않는다.
  private handleForcedLogout() {
    if (this.forcedOut) return;
    this.forcedOut = true;
    this.scene.start("AuthScene", { notice: DUPLICATE_LOGIN_MSG });
  }

  private confirmLogout() {
    this.showConfirm(
      "정말 퇴근하시겠습니까?\n진행 상황은 자동 저장됩니다. 내일 또 출근하면 되니까요.",
      () => void this.logout()
    );
  }

  private async logout() {
    try {
      this.room?.leave();
    } catch { /* already disconnected */ }
    try {
      await apiFetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch { /* ignore network error, still return to auth */ }
    this.scene.start("AuthScene");
  }

  private cleanup() {
    setOnUnauthorized(null); // Auth/Character 씬의 정상 401에 반응하지 않게 해제
    // ScaleManager/window는 씬 재시작에도 살아남으므로 파괴된 조이스틱을
    // 가리키는 리스너를 직접 떼어낸다 (조이스틱 오브젝트 자체는 씬이 파괴).
    this.scale.off("resize", this.scheduleJoystickLayout, this);
    if (this.viewportChangeHandler) {
      window.removeEventListener("orientationchange", this.viewportChangeHandler);
      window.visualViewport?.removeEventListener("resize", this.viewportChangeHandler);
      this.viewportChangeHandler = null;
    }
    this.joyLayoutPending = false;
    this.joystick = null;
    try { this.room?.removeAllListeners(); this.room?.leave(); } catch { /* noop */ }
    this.otherPlayers.forEach((p) => p.container.destroy());
    this.otherPlayers.clear();
    for (const el of this.domNodes) el.remove();
    this.domNodes = [];
  }

  private pushChat(nickname: string, text: string, isSystem = false) {
    const line = document.createElement("div");
    const nameColor = isSystem ? "#aaaaaa" : "#ffd700";
    line.innerHTML = `<span style="color:${nameColor}">[${escapeHtml(nickname)}]</span> ${escapeHtml(text)}`;
    this.chatBox.appendChild(line);
    // keep max 60 lines
    while (this.chatBox.children.length > 60) {
      this.chatBox.removeChild(this.chatBox.firstChild!);
    }
    this.chatBox.scrollTop = this.chatBox.scrollHeight;

    // 모바일 접힘 중 도착한 플레이어 메시지 — 안 읽은 배지 + 1줄 미리보기.
    // 시스템 메시지는 별도 토스트가 있으므로 배지에 세지 않는다.
    if (this.isTouchUi && !this.chatOpen && !isSystem) {
      this.chatUnread++;
      this.updateChatBadge();
      this.showChatPreview(nickname, text);
    }
  }

  // ─── Server ──────────────────────────────────────────────────────────────

  private async connectServer() {
    try {
      const client = new Client(SERVER_URL);
      this.room = await client.joinOrCreate(this.zone, { token: this.joinToken });

      this.pushChat("System", `서버 접속 완료 (${this.room.sessionId})`, true);
      console.log("[CLIENT] joined room:", this.room.roomId, "| session:", this.room.sessionId);
      // Dev/debug handle (used by automated tests)
      (window as unknown as Record<string, unknown>).__scene = this;

      // 중복 접속으로 서버가 이 클라이언트를 끊는 경우 — 안내 후 로그인 화면.
      // 사유 메시지가 leave보다 먼저 오지만, 메시지 유실에 대비해 leave 코드도 본다.
      this.room.onMessage("force_logout", () => this.handleForcedLogout());
      this.room.onLeave((code: number) => {
        if (code === DUPLICATE_LOGIN_LEAVE_CODE) this.handleForcedLogout();
      });

      // chat은 state와 무관하므로 즉시 등록
      this.room.onMessage("chat", (data: IChatMessage) => {
        this.pushChat(data.nickname, data.text);
      });

      this.room.onMessage("levelup", (data: { level: number }) => {
        this.showToast(`🎉 레벨 업!\nLv. ${data.level} 달성`, "#f6c945", 2500);
        this.pushChat("System", `레벨 업! 이제 Lv. ${data.level} 이다.`, true);
      });

      this.room.onMessage("stamina", (data: { value: number; max: number }) => {
        this.updateStaminaHud(data.value, data.max);
      });

      this.room.onMessage("gold", (data: { value: number; delta?: number }) => {
        this.updateGoldHud(data.value);
        if (data.delta) {
          const sign = data.delta > 0 ? "+" : "";
          const color = data.delta > 0 ? "#f6c945" : "#e74c3c";
          this.floatText(this.localPlayer.x, this.localPlayer.y - 16, `${sign}${data.delta}G`, color);
        }
      });

      this.room.onMessage("stamina_error", (data: { message: string }) => {
        this.showToast(data.message, "#e74c3c", 1800);
        this.pushChat("System", data.message, true);
      });

      this.room.onMessage("rank", (data: { value: string }) => {
        this.rank = data.value;
        this.updateHud(this.lastLevel, this.lastExp);
      });

      this.room.onMessage("promoted", (data: { rank: string }) => {
        this.rank = data.rank;
        this.updateHud(this.lastLevel, this.lastExp);
        this.showToast(`🎉 전직 시험 합격!\n"${data.rank}"로 승진했습니다!\n집중력 최대치 +10`, "#f6c945", 3500);
        this.pushChat("System", `전직 시험 합격! 이제 ${data.rank}다.`, true);
      });

      this.room.onMessage("hired", (data: { companyName: string }) => {
        this.showToast(`🏢 "${data.companyName}"에 입사했습니다! 축하합니다.`, "#2ecc71", 2800);
        this.pushChat("System", `"${data.companyName}"에 입사했습니다.`, true);
      });

      this.room.onMessage("job_changed", (data: {
        fromCompanyName: string; toCompanyName: string; salary: number;
      }) => {
        this.pushChat("System", `"${data.fromCompanyName}" → "${data.toCompanyName}" 이직 완료. 주급 ${data.salary}G.`, true);
      });

      this.room.onMessage("performance", (data: { value: number; delta?: number }) => {
        if (data.delta) {
          const sign = data.delta > 0 ? "+" : "";
          const color = data.delta > 0 ? "#2ecc71" : "#e74c3c";
          this.showToast(`📊 고과 점수 ${sign}${data.delta} (현재 ${data.value})`, color, 2000);
        }
      });

      this.room.onMessage("project_state", (data: {
        main: ActiveProjectView | null; side: ActiveProjectView | null;
      }) => {
        this.applyProjectStates(data.main, data.side);
      });

      this.room.onMessage("daily_state", (data: DailyQuestsState) => {
        this.dailyState = data;
        this.updateDailyBadge();
        if (this.openPanel === "daily") this.renderDailyTracker();
      });

      this.room.onMessage("daily_progress", (data: { label: string }) => {
        this.showToast(`📋 데일리 완료: ${data.label}!\n대시보드에서 보상을 받으세요.`, "#5dade2", 2500);
      });

      this.room.onMessage("side_job_resigned", (data: { companyName: string | null }) => {
        this.showToast(`🚪 "${data.companyName ?? "회사"}"에 사직서를 던졌다!\n부업이 본업이 되었다.`, "#f6c945", 3000);
        if (this.projectPanelOpen) void this.loadProjectMarket();
      });

      this.room.onMessage("project_log", (data: { lines: string[] }) => {
        this.pushBattleLog(data.lines);
      });

      this.room.onMessage("skill_completed", (data: { name: string }) => {
        this.showToast(`📚 "${data.name}" 학습 완료!`, "#2ecc71", 2200);
        this.pushBattleLog([`✅ "${data.name}" 스킬 학습을 완료했습니다.`]);
        if (this.skillPanelOpen) void this.loadSkills();
      });

      this.room.onMessage("codex_entry", (data: {
        templateId: string; label: string; codex: string; discoveredCount: number; totalCount: number;
      }) => {
        this.showToast(`📖 도감 등록: ${data.label}\n(${data.discoveredCount}/${data.totalCount})`, "#5dade2", 2500);
        this.pushBattleLog([`📖 도감 등록 — ${data.label}`, `"${data.codex}"`]);
        if (this.codexPanelOpen) void this.loadCodex();
      });

      this.room.onMessage("codex_complete", (data: { coffee: number; title: string }) => {
        this.showToast(`🏆 도감 완성!\n칭호 "${data.title}" 획득\n☕ 커피 ${data.coffee}개 지급`, "#f6c945", 3500);
        this.pushBattleLog([`🏆 도감 완성! 칭호 "${data.title}"를 획득했다.`, `☕ 보상: 커피 ${data.coffee}개`]);
        if (this.codexPanelOpen) void this.loadCodex();
      });

      this.room.onMessage("travel_result", (data: { ok: boolean; toZone?: ZoneId; message?: string }) => {
        if (!data.ok) {
          this.showToast(data.message ?? "이동할 수 없습니다.", "#e74c3c", 2000);
          return;
        }
        void this.completeTravel(data.toZone!);
      });

      // ROOM_STATE 수신 후 onAdd 등록 — 이 시점엔 players가 채워져 있어
      // triggerAll(=true)이 기존 플레이어를 즉시 순회한다
      this.room.onStateChange.once(() => {
        const players = this.room.state.players;
        console.log("[CLIENT] onStateChange.once fired | players in room:", players.size);

        players.onAdd((player: any, sessionId: string) => {
          console.log("[CLIENT] onAdd fired:", sessionId,
            "| x:", player.x, "y:", player.y,
            "| me?", sessionId === this.room.sessionId);

          if (sessionId === this.room.sessionId) {
            this.localPlayer.setPosition(player.x, player.y);
            this.localPlayer.setDepth(player.y);
            this.lastSentPos = { x: player.x, y: player.y };
            this.updateHud(player.level, player.exp);
            this.setHpBar(this.localHpFill, player.hp, player.maxHp);
            this.updateHpHud(player.hp, player.maxHp);
            player.listen("level", (v: number) => this.updateHud(v, player.exp));
            player.listen("exp",   (v: number) => this.updateHud(player.level, v));
            player.listen("hp",    (v: number) => {
              this.setHpBar(this.localHpFill, v, player.maxHp);
              this.updateHpHud(v, player.maxHp);
            });
            player.listen("maxHp", (v: number) => {
              this.setHpBar(this.localHpFill, player.hp, v);
              this.updateHpHud(player.hp, v);
            });
            // Server can teleport the local player (respawn on death).
            // Only snap on big jumps — snapping on every echoed move would
            // rubber-band the smooth local movement back by one RTT.
            const SNAP_DIST = TILE_SIZE * 2;
            player.listen("x", (v: number) => {
              if (Math.abs(v - this.localPlayer.x) > SNAP_DIST) {
                this.localPlayer.setX(v);
                this.lastSentPos.x = v;
              }
            });
            player.listen("y", (v: number) => {
              if (Math.abs(v - this.localPlayer.y) > SNAP_DIST) {
                this.localPlayer.setY(v);
                this.localPlayer.setDepth(v);
                this.lastSentPos.y = v;
              }
            });
            return;
          }

          this.spawnOtherPlayer(sessionId, player.x, player.y, player.nickname, player.job);
          console.log("[CLIENT] spawned OTHER player | total others:", this.otherPlayers.size);

          player.listen("x", (val: number) => {
            this.otherPlayers.get(sessionId)?.container.setX(val);
          });
          player.listen("y", (val: number) => {
            const p = this.otherPlayers.get(sessionId);
            if (p) { p.container.setY(val); p.container.setDepth(val); }
          });
          player.listen("hp", (v: number) => {
            const p = this.otherPlayers.get(sessionId);
            if (p) this.setHpBar(p.hpFill, v, player.maxHp);
          });
        });

        players.onRemove((_: any, sessionId: string) => {
          this.removeOtherPlayer(sessionId);
        });

        // Monsters
        const monsters = this.room.state.monsters;
        monsters.onAdd((m: any, id: string) => {
          this.spawnMonster(id, m.templateId, m.x, m.y, m.hp, m.maxHp, m.alive);
          m.listen("x",     (v: number) => this.updateMonster(id, { x: v }));
          m.listen("y",     (v: number) => this.updateMonster(id, { y: v }));
          m.listen("hp",    (v: number) => this.updateMonster(id, { hp: v }));
          m.listen("alive", (v: boolean) => this.updateMonster(id, { alive: v }));
        });
        monsters.onRemove((_: any, id: string) => {
          this.monsters.get(id)?.container.destroy();
          this.monsters.delete(id);
        });
      });

      this.room.onMessage("hunt_result", (d: {
        ok: boolean; reason?: string; monsterId?: string; monsterLabel?: string;
        outcome?: { crit: boolean; damageDealt: number; killed: boolean; damageTaken: number };
        expGained?: number; goldGained?: number; log?: string[];
      }) => {
        if (!d.ok) {
          this.floatText(this.localPlayer.x, this.localPlayer.y - 6, "대상 없음", "#aaaaaa");
          return;
        }
        const { outcome, log } = d;
        if (log) this.pushBattleLog(log);

        const view = d.monsterId ? this.monsters.get(d.monsterId) : undefined;
        if (view && outcome) {
          const color = outcome.crit ? "#f6c945" : "#ff5555";
          this.floatText(view.container.x, view.container.y - 8, `-${outcome.damageDealt}`, color);
          this.tweens.add({
            targets: view.container, scaleX: 1.25, scaleY: 0.8,
            yoyo: true, duration: 80,
          });
        }
        if (outcome?.killed) {
          this.showToast(`${d.monsterLabel ?? "몬스터"} 처치! +${d.expGained ?? 0} EXP, +${d.goldGained ?? 0} G`, "#27c93f", 1500);
        } else if (outcome && outcome.damageTaken > 0) {
          this.floatText(this.localPlayer.x, this.localPlayer.y - 6, `-${outcome.damageTaken}`, "#ff5555");
        }
      });

      this.room.onMessage("died", () => {
        this.showToast("쓰러졌다... 부활!", "#e74c3c", 1800);
        this.pushChat("System", "HP가 0이 되어 안전한 곳에서 부활했다.", true);
      });

      this.room.onMessage("item_used", (data: {
        ok: boolean; itemId?: string; remaining?: number; reason?: "no_item" | "daily_limit";
        buff?: { multiplier: number; durationMs: number }; staminaGained?: number;
      }) => {
        this.applyItemUsed(data);
      });

      this.room.onMessage("quest_state", (data: { quests: QuestMap }) => {
        this.applyQuestState(data.quests ?? {});
      });

    } catch (err) {
      console.error(err);
      this.pushChat("System", "서버 연결 실패. 서버가 실행 중인지 확인하세요.", true);
    }
  }

  // ─── Zones ───────────────────────────────────────────────────────────────

  private showZoneBanner(name: string) {
    const el = document.createElement("div");
    el.textContent = name;
    el.style.cssText = [
      "position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:145",
      "background:rgba(0,0,0,0.7);color:#fff;padding:8px 28px;border-radius:20px",
      "font:700 20px monospace;letter-spacing:2px;pointer-events:none;transition:opacity 0.5s",
    ].join(";");
    document.body.appendChild(this.trackDom(el));
    this.time.delayedCall(2000, () => {
      el.style.opacity = "0";
      this.time.delayedCall(500, () => el.remove());
    });
  }

  // ─── NPC proximity (dialogue) ────────────────────────────────────────────

  private checkNpcProximity() {
    // Only NPCs with dialogue lines open a conversation on approach
    const px = this.localPlayer.x;
    const py = this.localPlayer.y;

    if (this.activeDialogue) {
      // Close if the player walks away from the NPC they're talking to
      const npc = this.activeDialogue.npc;
      if (Phaser.Math.Distance.Between(px, py, npc.x, npc.y) > npc.interactRange + 8) {
        this.closeDialogue();
      }
      return;
    }

    for (const npc of NPCS) {
      if (npc.zone !== this.zone || npc.lines.length === 0) continue;
      const inRange = Phaser.Math.Distance.Between(px, py, npc.x, npc.y) <= npc.interactRange;
      if (!inRange) {
        // Walked away — allow this NPC's dialogue to trigger again later
        this.dismissedNpcs.delete(npc.id);
        continue;
      }
      if (!this.dismissedNpcs.has(npc.id)) {
        this.openDialogue(npc);
        break;
      }
    }
  }

  // ─── Update ──────────────────────────────────────────────────────────────

  private movementBlocked(): boolean {
    // 사이드 팝업 열림, 또는 모바일에서 채팅 펼침 상태 (PC 채팅은 상시 펼침이라 제외)
    return this.openPanel !== null || (this.isTouchUi && this.chatOpen);
  }

  update(_time: number, delta: number) {
    // Hunt (E) / inventory (I) are event-driven — see setupInput()
    if (this.dashboardOpen) return;

    this.checkNpcProximity();
    this.checkHuntTarget();

    // Expire speed buff
    if (this.speedMultiplier !== 1 && this.time.now >= this.speedBuffUntil) {
      this.speedMultiplier = 1;
    }

    // 팝업 열림 중에는 캔버스 이동 입력(키보드·조이스틱)을 무시한다
    if (this.movementBlocked()) return;

    const speed = PLAYER_SPEED * this.speedMultiplier * (delta / 1000);
    let dx = 0;
    let dy = 0;

    if (this.cursors.left.isDown  || this.wasd.left.isDown)  dx -= speed;
    if (this.cursors.right.isDown || this.wasd.right.isDown) dx += speed;
    if (this.cursors.up.isDown    || this.wasd.up.isDown)    dy -= speed;
    if (this.cursors.down.isDown  || this.wasd.down.isDown)  dy += speed;

    // 가상 조이스틱 — 키보드 입력이 있으면 키보드 우선. 아날로그 방향은 그대로
    // 쓰되 속도는 키보드와 동일하게 고정한다 (힘 크기에 따른 가변 속도 없음).
    if (dx === 0 && dy === 0 && this.joystick && this.joystick.force > 0) {
      const f = this.joystick.force;
      dx = (this.joystick.forceX / f) * speed;
      dy = (this.joystick.forceY / f) * speed;
    }

    if (dx !== 0 || dy !== 0) {
      // Axis-separated collision so the player slides along walls
      const curX = this.localPlayer.x;
      const curY = this.localPlayer.y;
      let nx = curX;
      let ny = curY;

      if (dx !== 0 && !isSolidAt(this.zone, curX + dx, curY)) nx = curX + dx;
      if (dy !== 0 && !isSolidAt(this.zone, nx, curY + dy)) ny = curY + dy;

      if (nx !== curX || ny !== curY) {
        this.localPlayer.setPosition(nx, ny);
        this.localPlayer.setDepth(ny); // y-sort against buildings

        // Throttle: only send if moved > 2px
        const dist = Phaser.Math.Distance.Between(nx, ny, this.lastSentPos.x, this.lastSentPos.y);
        if (dist > 2 && this.room) {
          this.room.send("move", { x: nx, y: ny });
          this.lastSentPos = { x: nx, y: ny };
        }
      }
    }
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
